import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Container,
  Typography,
  Tabs,
  Tab,
  Box,
  Button,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { Check, KeyboardArrowDown } from "@mui/icons-material";

import Header from "./components/Header";
import CompanyCard from "./components/CompanyCard";
import SearchBar from "./components/SearchBar";
import Filters from "./components/Filters";
import SortBar from "./components/SortBar";
import ResetFilters from "./components/ResetFilters";
import Overview from "./components/Overview";
import TabMiniCharts from "./components/TabMiniCharts";
import UpdatedTimeline from "./components/UpdatedTimeline";
import Login from "./components/Login";
import CriteriaExtractionPanel, {
  buildCriteriaRows,
  createCountColumns,
} from "./components/CriteriaExtractionPanel";
import NetworkGraph from "./features/network-graph/NetworkGraph";
import GlobaldataWorkspace from "./features/globaldata/GlobaldataWorkspace";
import CompanyHouseWorkspace from "./features/companyhouse/CompanyHouseWorkspace";
import {
  MultiSelectionAutocomplete,
  SingleSelectionAutocomplete,
} from "./components/SelectionAutocomplete";
import { normalizeFundingStage } from "./utils/fundingStages";

import exportFeedbackToWorkbook from "./helpers/exportFeedbackToWorkbook.js";
import {
  getCrunchbasePeopleRoleValue,
} from "./helpers/crunchbasePeopleRoles";
import {
  CRUNCHBASE_EXPORT_FIELD_OPTIONS,
  DEFAULT_CRUNCHBASE_EXPORT_FIELDS,
} from "./helpers/exportFieldOptions";
import {
  EXPORT_FORMAT_OPTIONS,
  EXPORT_FORMAT_XLSX,
  getExportDownloadLabel,
} from "./helpers/exportFileFormats";
import { buildExportFilterRows } from "./helpers/exportFilterSummary";
import {
  buildAssignmentDatasetNameLookup,
  buildAssignmentCanonicalNameLookup,
  normalizeAssignmentCompanyKey,
  resolveAssignmentNamesForDataset,
  resolveCanonicalAssignmentCompanyName,
} from "./helpers/companyAssignmentKeys";

const DATA_SOURCE = (process.env.REACT_APP_DATA_SOURCE || "static").toLowerCase();
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const AUTH_API_BASE_URL = (
  process.env.REACT_APP_AUTH_API_BASE_URL || "/auth"
).replace(/\/$/, "");
const AUTH_SESSION_STORAGE_KEY = "lead_auth_session";
const WORKSPACE_DATASET_STORAGE_KEY = "lead_workspace_dataset";
const AUTH_FLOW_LOGIN = "login";
const AUTH_FLOW_FORGOT_PASSWORD = "forgot-password";
const AUTH_FLOW_RESET_PASSWORD = "reset-password";
const COMPANIES_API_PATH =
  process.env.REACT_APP_COMPANIES_API_PATH || "/api/lead-details/all";
const UPDATE_WINDOW_API_PATH = "/api/lead-details/update-window";

const BUCKET_MAP = {
  biotechnology: "Biotech",
  "life science": "Biotech",
  oncology: "Biotech",
  therapeutics: "Biotech",

  pharmaceutical: "Pharmaceutical",

  "health care": "Healthcare",
  medical: "Healthcare",

  "artificial intelligence": "Artificial Intelligence",
  "artificial intelligence (ai)": "Artificial Intelligence",

  agtech: "AgTech",
  agriculture: "AgTech",
  "animal feed": "AgTech",
  "animal health": "AgTech"
};

function normalizeBuckets(industries) {
  if (!industries) return [];
  const list = Array.isArray(industries)
    ? industries
    : typeof industries === "string"
    ? industries.split(",")
    : [];

  const cleaned = list
    .filter((x) => typeof x === "string")
    .map((x) => x.trim().toLowerCase());

  const buckets = new Set();
  cleaned.forEach((x) => {
    if (BUCKET_MAP[x]) buckets.add(BUCKET_MAP[x]);
  });

  return Array.from(buckets);
}

function normalizeCountry(location) {
  if (!location || typeof location !== "string") return "Unknown";
  const parts = location.split(",").map((p) => p.trim());
  return parts[parts.length - 1] || "Unknown";
}

function getFundingMonth(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString("en-GB", { month: "long" });
}

function getFundingQuarter(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";

  return `Q${Math.floor(date.getMonth() / 3) + 1}`;
}

function getFundingYear(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";

  return String(date.getFullYear());
}

function SummaryCard({ label, value, tone = "default", onClick }) {
  const toneMap = {
    default: {
      background: "#ffffff",
      color: "#0f172a",
      border: "rgba(15, 23, 42, 0.06)",
    },
    matched: {
      background: "#ffffff",
      color: "#0f172a",
      border: "rgba(15, 23, 42, 0.06)",
    },
    unmatched: {
      background: "#ffffff",
      color: "#0f172a",
      border: "rgba(15, 23, 42, 0.06)",
    },
  };

  const palette = toneMap[tone] || toneMap.default;

  return (
    <Paper
      onClick={onClick}
      sx={{
        p: 1.25,
        borderRadius: 3,
        backgroundColor: palette.background,
        color: palette.color,
        border: `1px solid ${palette.border}`,
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <Typography
        variant="body1"
        sx={{
          mb: 0.45,
          fontWeight: 600,
          color: "#1f2937",
        }}
      >
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
        {typeof value === "number" ? value.toLocaleString("en-GB") : value}
      </Typography>
    </Paper>
  );
}

function buildUrlForBase(baseUrl, path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  if (!baseUrl) return path;
  return `${baseUrl}${path.startsWith("/") ? path : `/${path}`}`;
}

function buildApiUrl(path) {
  return buildUrlForBase(API_BASE_URL, path);
}

function buildAuthApiUrl(path) {
  return buildUrlForBase(AUTH_API_BASE_URL, path);
}

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function getUserRoles(user) {
  const roles = Array.isArray(user?.roles)
    ? user.roles
    : user?.role
    ? [user.role]
    : [];

  return roles
    .map((role) => String(role || "").trim().toUpperCase())
    .filter(Boolean);
}

function isBdAdminUser(user) {
  return getUserRoles(user).includes("BD_ADMIN");
}

function isAdminUser(user) {
  return getUserRoles(user).includes("ADMIN");
}

function normalizeLeadAssignment(entry) {
  if (!entry || typeof entry !== "object") return null;

  const companyName = String(entry.companyName || "").trim();
  const assignedTo = normalizeEmail(entry.assignedTo);
  const status = String(entry.status || "").trim().toUpperCase();

  if (!companyName || status !== "ASSIGNED" || !assignedTo) {
    return null;
  }

  return {
    companyName,
    assignedBy: normalizeEmail(entry.assignedBy),
    assignedTo,
    status,
    comment: String(entry.comment || "").trim(),
    createdAt: entry.createdAt || null,
  };
}

function normalizeCommentNotificationItem(entry) {
  if (!entry || typeof entry !== "object") return null;

  const id = String(entry.id || "").trim();
  const leadContext = String(entry.leadContext || "").trim().toUpperCase();
  const organizationName = String(entry.organizationName || "").trim();
  const userId = normalizeEmail(entry.userId);
  const message = String(entry.message || "").trim();

  if (!id || !leadContext || !organizationName || !userId || !message) {
    return null;
  }

  return {
    id,
    leadContext,
    organizationName,
    userId,
    message,
    createdAt: entry.createdAt || null,
  };
}

function normalizeCommentNotificationResponse(payload) {
  const unreadCount = Number(payload?.unreadCount || 0);
  return {
    unreadCount: Number.isFinite(unreadCount) && unreadCount > 0 ? unreadCount : 0,
    lastSeenAt: payload?.lastSeenAt || null,
    evaluatedAt: payload?.evaluatedAt || null,
    items: Array.isArray(payload?.items)
      ? payload.items.map(normalizeCommentNotificationItem).filter(Boolean)
      : [],
  };
}

function mapAssignmentsByCompany(assignments) {
  return assignments.reduce((acc, assignment) => {
    const assignmentKey = normalizeAssignmentCompanyKey(assignment.companyName);
    if (!assignmentKey) {
      return acc;
    }

    acc[assignmentKey] = assignment;
    return acc;
  }, {});
}

function addDaysToIsoDate(isoDate, days) {
  const nextDate = new Date(`${isoDate}T00:00:00Z`);
  nextDate.setUTCDate(nextDate.getUTCDate() + days);
  return nextDate.toISOString().slice(0, 10);
}

async function fetchJson(url) {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: ${response.status}`);
  }

  return response.json();
}

async function parseResponseJson(response, fallbackMessage) {
  const contentType = response.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const payload = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const message =
      typeof payload === "string"
        ? payload
        : Array.isArray(payload?.detail)
        ? payload.detail
            .map((entry) => entry?.msg || entry?.message || "")
            .filter(Boolean)
            .join(" ")
        : payload?.message ||
          payload?.error ||
          payload?.detail?.message ||
          payload?.detail ||
          fallbackMessage;
    throw new Error(message);
  }

  return payload;
}

function loadStoredAuthSession() {
  try {
    const raw = localStorage.getItem(AUTH_SESSION_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed?.token || !parsed?.user) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function persistAuthSession(session) {
  localStorage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(session));
  localStorage.setItem("lead_auth", "true");
}

function persistWorkspaceDataset(dataset) {
  try {
    localStorage.setItem(WORKSPACE_DATASET_STORAGE_KEY, dataset);
  } catch {
    // Ignore storage failures and fall back to the in-memory state.
  }
}

function loadStoredCrunchbaseWorkspaceConfig() {
  try {
    const raw = localStorage.getItem("crunchbase_workspace_state");
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    const configFields = SAVED_SEARCH_CONFIG_FIELDS_BY_DATASET[SAVED_SEARCH_DATASET_CRUNCHBASE];
    return configFields.reduce((acc, fieldName) => {
      const fieldValue = parsed[fieldName];
      acc[fieldName] = Array.isArray(fieldValue) ? fieldValue : fieldValue || "";
      return acc;
    }, {});
  } catch {
    return null;
  }
}

function persistStoredCrunchbaseWorkspaceConfig(config) {
  try {
    localStorage.setItem("crunchbase_workspace_state", JSON.stringify(config));
  } catch {
    // Ignore storage failures and fall back to in-memory state.
  }
}

function clearStoredWorkspaceCompanySearchState() {
  try {
    ["globaldata_workspace_state", "companyhouse_workspace_state"].forEach((storageKey) => {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return;

      localStorage.setItem(
        storageKey,
        JSON.stringify({
          ...parsed,
          searchTerm: "",
          selectedGraphCompanyNames: [],
        })
      );
    });
  } catch {
    // Ignore storage failures and fall back to current in-memory state.
  }
}

function buildClearedCrunchbaseWorkspaceConfig({
  currentTab = "OVERVIEW",
  updatedWindowFrom = "",
  updatedWindowTo = "",
} = {}) {
  return {
    currentTab,
    updatedWindowFrom,
    updatedWindowTo,
    searchTerm: "",
    selectedGraphCompanyNames: [],
    selectedIndustry: [],
    selectedCountry: [],
    selectedStage: [],
    selectedUpdateCategory: [],
    selectedEstimatedRevenueRange: [],
    selectedCompanyAge: [],
    selectedPeopleRoles: [],
    selectedInvestorType: [],
    selectedFundingPeriod: [],
    selectedFundingYear: [],
    selectedLeadStatus: [],
    selectedManualChange: "",
    matchedState: "all",
    clientState: "all",
    sortBy: "",
  };
}

function clearStoredAuthSession() {
  localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
  localStorage.removeItem("lead_auth");
}

function loadAuthFlowFromSearch(search) {
  const params = new URLSearchParams(search);
  const authMode = String(params.get("auth") || "").trim().toLowerCase();
  const token = String(params.get("token") || "").trim();

  if (authMode === AUTH_FLOW_FORGOT_PASSWORD) {
    return { mode: AUTH_FLOW_FORGOT_PASSWORD, token: "" };
  }

  if (authMode === AUTH_FLOW_RESET_PASSWORD) {
    return { mode: AUTH_FLOW_RESET_PASSWORD, token };
  }

  return { mode: AUTH_FLOW_LOGIN, token: "" };
}

function buildSearchForAuthFlow(currentSearch, authFlow) {
  const params = new URLSearchParams(currentSearch);

  if (!authFlow || authFlow.mode === AUTH_FLOW_LOGIN) {
    params.delete("auth");
    params.delete("token");
  } else {
    params.set("auth", authFlow.mode);
    if (authFlow.mode === AUTH_FLOW_RESET_PASSWORD && authFlow.token) {
      params.set("token", authFlow.token);
    } else {
      params.delete("token");
    }
  }

  const query = params.toString();
  return query ? `?${query}` : "";
}

function syncAuthFlowInLocation(authFlow, { replace = false } = {}) {
  const nextSearch = buildSearchForAuthFlow(window.location.search, authFlow);
  const nextUrl = `${window.location.pathname}${nextSearch}${window.location.hash}`;
  const historyMethod = replace ? "replaceState" : "pushState";
  window.history[historyMethod](null, "", nextUrl);
}

const SAVED_SEARCHES_KEY = "lead_saved_searches";
const SAVED_SEARCH_DATASET_CRUNCHBASE = "crunchbase";
const SAVED_SEARCH_DATASET_GLOBALDATA = "globaldata";
const WORKSPACE_DATASET_COMPANYHOUSE = "companyhouse";
const QUARTER_OPTIONS = ["Q1", "Q2", "Q3", "Q4"];
const LEAD_TYPE_SORT_ORDER = {
  ACCEPT: 0,
  FUZZY: 1,
  "NOT ACCEPT": 2,
};
const VALID_TABS = new Set([
  "OVERVIEW",
  "NEW_ADDITIONS",
  "ACCEPT",
  "NOT ACCEPT",
  "FUZZY",
  "ARCHIVED",
  "ASSIGNMENTS",
]);
const VALID_RIGHT_PANEL_VIEWS = new Set(["visualisations", "companies", "network"]);
const VALID_LEAD_STATUSES = new Set(["ACCEPT", "NOT ACCEPT", "FUZZY"]);
const VALID_UPDATE_CATEGORIES = new Set([
  "New Company",
  "Updates",
  "Reclassified",
  "Archived",
]);
const MATCH_STATE_ALL = "all";
const MATCH_STATE_GLOBALDATA_MATCHED = "globaldata_matched";
const MATCH_STATE_GLOBALDATA_UNMATCHED = "globaldata_unmatched";
const MATCH_STATE_COMPANYHOUSE_MATCHED = "companyhouse_matched";
const MATCH_STATE_COMPANYHOUSE_UNMATCHED = "companyhouse_unmatched";
const MATCH_STATE_ALL_UNMATCHED = "all_unmatched";
const VALID_MATCH_STATES = new Set([
  MATCH_STATE_ALL,
  MATCH_STATE_GLOBALDATA_MATCHED,
  MATCH_STATE_GLOBALDATA_UNMATCHED,
  MATCH_STATE_COMPANYHOUSE_MATCHED,
  MATCH_STATE_COMPANYHOUSE_UNMATCHED,
  MATCH_STATE_ALL_UNMATCHED,
]);
const CLIENT_STATE_ALL = "all";
const CLIENT_STATE_CLIENT = "client";
const CLIENT_STATE_CONVERTED = "converted";
const CLIENT_STATE_CONTACTED = "contacted";
const CLIENT_STATE_NON_CLIENT = "non-client";
const CLIENT_STATE_NOT_CLIENT_ANYMORE = "not-client-anymore";
const CLIENT_LIFECYCLE_EXISTING = "EXISTING_CLIENT";
const CLIENT_LIFECYCLE_CONVERTED = "CONVERTED_CLIENT";
const CLIENT_LIFECYCLE_NOT_CLIENT_ANYMORE = "NOT_CLIENT_ANYMORE";
const CLIENT_LIFECYCLE_NON_CLIENT = "NON_CLIENT";
const VALID_CLIENT_STATES = new Set([
  CLIENT_STATE_ALL,
  CLIENT_STATE_CLIENT,
  CLIENT_STATE_CONVERTED,
  CLIENT_STATE_CONTACTED,
  CLIENT_STATE_NON_CLIENT,
  CLIENT_STATE_NOT_CLIENT_ANYMORE,
]);
const CRUNCHBASE_MATCH_STATUS_OPTIONS = [
  { value: MATCH_STATE_ALL, label: "All" },
  { value: MATCH_STATE_GLOBALDATA_MATCHED, label: "Matched to GlobalData" },
  { value: MATCH_STATE_GLOBALDATA_UNMATCHED, label: "Unmatched to GlobalData" },
  { value: MATCH_STATE_COMPANYHOUSE_MATCHED, label: "Matched to Company House" },
  { value: MATCH_STATE_COMPANYHOUSE_UNMATCHED, label: "Unmatched to Company House" },
  { value: MATCH_STATE_ALL_UNMATCHED, label: "Unmatched (All)" },
];
const CRUNCHBASE_LIST_STATUS_OPTIONS = [
  { value: CLIENT_STATE_ALL, label: "All" },
  { value: CLIENT_STATE_CLIENT, label: "Existing Client" },
  { value: CLIENT_STATE_CONVERTED, label: "Converted +" },
  { value: CLIENT_STATE_CONTACTED, label: "Contacted" },
  { value: CLIENT_STATE_NOT_CLIENT_ANYMORE, label: "Not client anymore" },
  { value: CLIENT_STATE_NON_CLIENT, label: "Non-client" },
];
const TRAILING_COMPANY_SUFFIX_PATTERN = /(?:\s+(?:ltd|limited|inc|corp|corporation|llc|plc|gmbh|sas|sa|spa|srl|bv|ag))+$/i;

const SAVED_SEARCH_CONFIG_FIELDS_BY_DATASET = {
  [SAVED_SEARCH_DATASET_CRUNCHBASE]: [
    "currentTab",
    "updatedWindowFrom",
    "updatedWindowTo",
    "searchTerm",
    "selectedGraphCompanyNames",
    "selectedIndustry",
    "selectedCountry",
    "selectedStage",
    "selectedUpdateCategory",
    "selectedEstimatedRevenueRange",
    "selectedCompanyAge",
    "selectedPeopleRoles",
    "selectedInvestorType",
    "selectedFundingPeriod",
    "selectedFundingYear",
    "selectedLeadStatus",
    "selectedManualChange",
    "matchedState",
    "clientState",
    "sortBy",
  ],
  [SAVED_SEARCH_DATASET_GLOBALDATA]: [
    "searchTerm",
    "matchedState",
    "selectedHeadquarters",
    "selectedEntityTypes",
    "selectedRelationshipTypes",
    "selectedCompanyStages",
    "selectedTherapyAreas",
    "selectedDrugDevelopmentStages",
    "selectedMoleculeTypes",
    "selectedDrugTypes",
    "selectedDrugGeographies",
    "selectedRouteOfAdministrations",
    "sortBy",
    "sortDirection",
  ],
};

function getSavedSearchDataset(value) {
  return value === SAVED_SEARCH_DATASET_GLOBALDATA
    ? SAVED_SEARCH_DATASET_GLOBALDATA
    : SAVED_SEARCH_DATASET_CRUNCHBASE;
}

function getSavedSearchConfigFields(dataset) {
  return SAVED_SEARCH_CONFIG_FIELDS_BY_DATASET[getSavedSearchDataset(dataset)];
}

function normalizeSavedSearchEntry(search) {
  if (!search || typeof search !== "object") return null;

  const name = String(search.name || "").trim();
  if (!name) return null;

  const dataset = getSavedSearchDataset(search.dataset);
  const configFields = getSavedSearchConfigFields(dataset);
  const sourceConfig = search.config && typeof search.config === "object" ? search.config : {};
  const config = configFields.reduce((acc, fieldName) => {
    const fieldValue = sourceConfig[fieldName];
    acc[fieldName] = Array.isArray(fieldValue) ? fieldValue : fieldValue || "";
    return acc;
  }, {});

  return { name, dataset, config };
}

function upsertSavedSearch(savedSearches, dataset, name, config) {
  const normalizedDataset = getSavedSearchDataset(dataset);
  const nextSearch = normalizeSavedSearchEntry({ name, dataset: normalizedDataset, config });
  if (!nextSearch) {
    return savedSearches;
  }

  return [
    ...savedSearches.filter(
      (search) =>
        !(
          getSavedSearchDataset(search.dataset) === normalizedDataset &&
          search.name === nextSearch.name
        )
    ),
    nextSearch,
  ].sort((a, b) => {
    const datasetComparison = getSavedSearchDataset(a.dataset).localeCompare(
      getSavedSearchDataset(b.dataset)
    );
    return datasetComparison !== 0
      ? datasetComparison
      : a.name.localeCompare(b.name);
  });
}

function normalizeSelection(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim() !== "") return [value];
  return [];
}

function normalizeKey(value) {
  return String(value || "").toLowerCase().replace(/\s+/g, " ").trim();
}

function normalizeQueryList(params, key, allowedValues) {
  return params
    .getAll(key)
    .map((value) => value.trim())
    .filter((value) => allowedValues.has(value));
}

function normalizeWorkspaceDatasetParam(value) {
  const normalizedValue = String(value || "").trim().toLowerCase();
  return normalizedValue === "globaldata" || normalizedValue === "companyhouse"
    ? normalizedValue
    : "crunchbase";
}

function normalizeMatchedStateParam(value) {
  const normalizedValue = String(value || "").trim().toLowerCase();
  return VALID_MATCH_STATES.has(normalizedValue) ? normalizedValue : MATCH_STATE_ALL;
}

function normalizeClientStateParam(value) {
  const normalizedValue = String(value || "").trim().toLowerCase();
  return VALID_CLIENT_STATES.has(normalizedValue) ? normalizedValue : CLIENT_STATE_ALL;
}

function getClientStateFilterLabel(value) {
  if (value === CLIENT_STATE_CLIENT) return "Existing client only";
  if (value === CLIENT_STATE_CONVERTED) return "Converted + only";
  if (value === CLIENT_STATE_CONTACTED) return "Contacted only";
  if (value === CLIENT_STATE_NOT_CLIENT_ANYMORE) return "Not client anymore only";
  if (value === CLIENT_STATE_NON_CLIENT) return "Non-client only";
  return "All";
}

function normalizeClientLifecycleStatus(value, hasSalesforceClientMatch) {
  const normalizedValue = String(value || "").trim().toUpperCase().replace(/-/g, "_");
  if (
    normalizedValue === CLIENT_LIFECYCLE_EXISTING ||
    normalizedValue === CLIENT_LIFECYCLE_CONVERTED ||
    normalizedValue === CLIENT_LIFECYCLE_NOT_CLIENT_ANYMORE ||
    normalizedValue === CLIENT_LIFECYCLE_NON_CLIENT
  ) {
    return normalizedValue;
  }
  if (hasSalesforceClientMatch) return CLIENT_LIFECYCLE_EXISTING;
  return CLIENT_LIFECYCLE_NON_CLIENT;
}

function getMatchTargetFromState(matchState, defaultTarget = "globaldata") {
  if (String(matchState || "").startsWith("companyhouse_")) {
    return "companyhouse";
  }
  if (String(matchState || "").startsWith("globaldata_")) {
    return "globaldata";
  }
  return defaultTarget;
}

function getMatchLabels(matchState, defaultTarget = "globaldata") {
  if (matchState === MATCH_STATE_ALL_UNMATCHED) {
    return {
      target: "all",
      matched: "Matched",
      unmatched: "Unmatched across all datasets",
    };
  }

  const target = getMatchTargetFromState(matchState, defaultTarget);
  const targetLabel = target === "companyhouse" ? "Company House" : "GlobalData";
  return {
    target,
    matched: `Matched to ${targetLabel}`,
    unmatched: `Unmatched to ${targetLabel}`,
  };
}

function normalizeIsoDateParam(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(value || "") ? value : "";
}

function compareIsoDates(leftIsoDate, rightIsoDate) {
  if (!leftIsoDate && !rightIsoDate) return 0;
  if (!leftIsoDate) return -1;
  if (!rightIsoDate) return 1;
  return leftIsoDate.localeCompare(rightIsoDate);
}

function minIsoDate(...isoDates) {
  return isoDates
    .filter((value) => normalizeIsoDateParam(value))
    .sort(compareIsoDates)[0] || "";
}

function maxIsoDate(...isoDates) {
  const normalizedDates = isoDates
    .filter((value) => normalizeIsoDateParam(value))
    .sort(compareIsoDates);

  return normalizedDates[normalizedDates.length - 1] || "";
}

function buildCrossWorkspaceSearchTerm(companyName) {
  const rawCompanyName = String(companyName || "").trim();
  if (!rawCompanyName) return "";

  const withoutDiacritics = rawCompanyName
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  let normalized = withoutDiacritics
    .replace(/&/g, " and ")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  while (TRAILING_COMPANY_SUFFIX_PATTERN.test(normalized)) {
    normalized = normalized.replace(TRAILING_COMPANY_SUFFIX_PATTERN, "").trim();
  }

  return normalized || rawCompanyName;
}

function isReloadNavigation() {
  try {
    const navigationEntries = window.performance?.getEntriesByType?.("navigation");
    if (Array.isArray(navigationEntries) && navigationEntries.length > 0) {
      return navigationEntries[0]?.type === "reload";
    }

    return window.performance?.navigation?.type === 1;
  } catch {
    return false;
  }
}

function parseInitialQueryConfig(search, options = {}) {
  if (!search) return null;

  const { ignoreGlobaldataDataset = false } = options;
  const params = new URLSearchParams(search);
  const requestedDataset = normalizeWorkspaceDatasetParam(params.get("dataset"));
  const shouldIgnoreGlobaldataParams =
    ignoreGlobaldataDataset && requestedDataset === "globaldata";
  const workspaceDataset =
    shouldIgnoreGlobaldataParams
      ? "crunchbase"
      : requestedDataset;
  const currentTab = shouldIgnoreGlobaldataParams ? "" : params.get("tab") || "";
  const rightPanelView = shouldIgnoreGlobaldataParams ? "" : params.get("view") || "";
  const updatedWindowFrom = shouldIgnoreGlobaldataParams
    ? ""
    : normalizeIsoDateParam(params.get("from"));
  const updatedWindowTo = shouldIgnoreGlobaldataParams
    ? ""
    : normalizeIsoDateParam(params.get("to"));
  const searchTerm = shouldIgnoreGlobaldataParams
    ? ""
    : (params.get("search") || params.get("company") || "").trim();
  const matchedState = shouldIgnoreGlobaldataParams
    ? ""
    : normalizeMatchedStateParam(params.get("matchedState"));
  const clientState = shouldIgnoreGlobaldataParams
    ? ""
    : normalizeClientStateParam(params.get("clientState"));
  const selectedLeadStatus = shouldIgnoreGlobaldataParams
    ? []
    : normalizeQueryList(params, "leadStatus", VALID_LEAD_STATUSES);
  const selectedUpdateCategory = shouldIgnoreGlobaldataParams
    ? []
    : normalizeQueryList(params, "updateCategory", VALID_UPDATE_CATEGORIES);

  const normalizedConfig = {
    workspaceDataset,
    currentTab: VALID_TABS.has(currentTab) ? currentTab : "",
    rightPanelView: VALID_RIGHT_PANEL_VIEWS.has(rightPanelView) ? rightPanelView : "",
    updatedWindowFrom,
    updatedWindowTo,
    searchTerm,
    matchedState,
    clientState,
    selectedLeadStatus,
    selectedUpdateCategory,
  };

  const explicitDatasetRequested = shouldIgnoreGlobaldataParams
    ? ""
    : String(params.get("dataset") || "").trim().toLowerCase();
  const explicitMatchedStateRequested = shouldIgnoreGlobaldataParams
    ? ""
    : String(params.get("matchedState") || "").trim().toLowerCase();
  const explicitClientStateRequested = shouldIgnoreGlobaldataParams
    ? ""
    : String(params.get("clientState") || "").trim().toLowerCase();

  const hasExplicitDatasetRequest =
    (explicitDatasetRequested === "globaldata" && !ignoreGlobaldataDataset) ||
    explicitDatasetRequested === "companyhouse";

  const hasAnyValue =
    hasExplicitDatasetRequest ||
    normalizedConfig.currentTab ||
    normalizedConfig.rightPanelView ||
    normalizedConfig.updatedWindowFrom ||
    normalizedConfig.updatedWindowTo ||
    normalizedConfig.searchTerm ||
    (VALID_MATCH_STATES.has(explicitMatchedStateRequested) &&
      explicitMatchedStateRequested !== "all") ||
    (VALID_CLIENT_STATES.has(explicitClientStateRequested) &&
      explicitClientStateRequested !== "all") ||
    normalizedConfig.selectedLeadStatus.length > 0 ||
    normalizedConfig.selectedUpdateCategory.length > 0;

  return hasAnyValue ? normalizedConfig : null;
}

function appendTextNode(doc, parent, tagName, value) {
  const element = doc.createElement(tagName);
  element.textContent = value ?? "";
  parent.appendChild(element);
}

function serializeSavedSearchesToXml(savedSearches) {
  const doc = document.implementation.createDocument("", "savedViews", null);
  const root = doc.documentElement;

  savedSearches.forEach((search) => {
    const normalizedSearch = normalizeSavedSearchEntry(search);
    if (!normalizedSearch) return;

    const savedViewElement = doc.createElement("savedView");
    savedViewElement.setAttribute("name", normalizedSearch.name);
    savedViewElement.setAttribute("dataset", normalizedSearch.dataset);

    getSavedSearchConfigFields(normalizedSearch.dataset).forEach((fieldName) => {
      const fieldValue = normalizedSearch.config?.[fieldName];

      if (Array.isArray(fieldValue)) {
        const fieldElement = doc.createElement(fieldName);
        fieldValue.forEach((value) => appendTextNode(doc, fieldElement, "value", value));
        savedViewElement.appendChild(fieldElement);
        return;
      }

      appendTextNode(doc, savedViewElement, fieldName, fieldValue || "");
    });

    root.appendChild(savedViewElement);
  });

  return new XMLSerializer().serializeToString(doc);
}

function getDirectChildElement(parent, tagName) {
  return Array.from(parent.children).find((child) => child.tagName === tagName) || null;
}

function parseSavedSearchesXml(savedFilterXml) {
  if (!savedFilterXml || typeof savedFilterXml !== "string" || !savedFilterXml.trim()) {
    return [];
  }

  const doc = new DOMParser().parseFromString(savedFilterXml, "application/xml");

  if (doc.getElementsByTagName("parsererror").length > 0) {
    console.error("Invalid savedFilterXml received from user configuration.");
    return [];
  }

  const savedViews = Array.from(doc.getElementsByTagName("savedView"));

  return savedViews
    .map((savedViewElement) => {
      const name = savedViewElement.getAttribute("name")?.trim() || "";
      const dataset = getSavedSearchDataset(savedViewElement.getAttribute("dataset"));
      if (!name) return null;

      const config = getSavedSearchConfigFields(dataset).reduce((acc, fieldName) => {
        const fieldElement = getDirectChildElement(savedViewElement, fieldName);
        if (!fieldElement) {
          acc[fieldName] = "";
          return acc;
        }

        if (Array.from(fieldElement.children).some((child) => child.tagName === "value")) {
          acc[fieldName] = Array.from(fieldElement.children)
            .filter((child) => child.tagName === "value")
            .map((child) => child.textContent || "")
            .filter((value) => value.trim() !== "");
          return acc;
        }

        acc[fieldName] = fieldElement.textContent || "";
        return acc;
      }, {});

      return { name, dataset, config };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const datasetComparison = a.dataset.localeCompare(b.dataset);
      return datasetComparison !== 0 ? datasetComparison : a.name.localeCompare(b.name);
    });
}

function matchesSearch(company, rawSearchTerm) {
  const search = rawSearchTerm.trim().toLowerCase();
  if (!search) return true;

  const searchableFields = [
    company["Organization Name"],
    company["Description"],
    company["Industries"],
    company["Investor Type"],
    company["Top 5 Investors"],
    company["Lead Investors"],
  ];

  return searchableFields.some((value) =>
    String(value || "").toLowerCase().includes(search)
  );
}

function matchesPeopleRoleFilter(company, selectedPeopleRoles) {
  if (selectedPeopleRoles.length === 0) {
    return true;
  }

  const selectedRoleValues = selectedPeopleRoles.map(getCrunchbasePeopleRoleValue);

  if (selectedRoleValues.includes("no_people_data") && !company.hasPeopleData) {
    return true;
  }

  const companyRoleBuckets = Array.isArray(company.peopleRoleBuckets)
    ? company.peopleRoleBuckets
    : [];

  return selectedRoleValues.some(
    (role) => role !== "no_people_data" && companyRoleBuckets.includes(role)
  );
}

function normalizeCompany(c) {
  const companyName = c.organizationName || c["Organization Name"];
  const classification = c.state || c["Scope classification"] || "";
  const explicitLeadType = c["Lead Type"] || "";
  const reasoning = c.stateJustification || c.Justification || c.Reasoning;
  const leadType =
    classification === "INTEREST"
      ? "ACCEPT"
      : classification === "NOT_INTEREST"
      ? "NOT ACCEPT"
      : explicitLeadType === "ACCEPT" ||
        explicitLeadType === "NOT ACCEPT" ||
        explicitLeadType === "FUZZY"
      ? explicitLeadType
      : "FUZZY";

  const rankString =
    c.cbRankCompany || c.cbRankOrganization || c["Crunchbase Rank"] || "";
  const rankNumber =
    typeof rankString === "string"
      ? parseInt(rankString.replace(/,/g, ""), 10)
      : typeof rankString === "number"
      ? rankString
      : null;
  const versionNumber =
    typeof c.version === "number"
      ? c.version
      : typeof c.Version === "number"
      ? c.Version
      : parseInt(String(c.version ?? c.Version ?? "0"), 10) || 0;
  const prospectChangeType =
    c.prospectChangeType || c["Prospect Change Type"] || null;
  const isNewAddition = prospectChangeType === "NEW";
  const isUpdatedCompany = prospectChangeType === "UPDATED";
  const classificationType =
    c.classificationType || c["Classification Type"] || null;
  const hasManualClassification =
    classificationType === "MANUAL" || classificationType === "RESTORED";
  const leadReclassified = Boolean(
    c.leadReclassified ?? c["Lead Reclassified"] ?? false
  );
  const updateCategories = [];
  if (isNewAddition) updateCategories.push("New Company");
  if (isUpdatedCompany) updateCategories.push("Updates");
  if (leadReclassified) updateCategories.push("Reclassified");
  const updateCategory = updateCategories[0] || "";
  const salesforceClientMatchingName =
    c.salesforceClientMatchingName || c["Salesforce Client Matching Name"] || "";
  const clientLifecycleStatus = normalizeClientLifecycleStatus(
    c.clientLifecycleStatus || c["Client Lifecycle Status"],
    Boolean(salesforceClientMatchingName)
  );
  return {
    id: companyName,
    "Organization Name": companyName,
    Description:
      c.description || c.fullDescription || c.Description || "Unknown",

    Industries: c.industries || c.Industries || [],
    BucketIndustries: normalizeBuckets(c.industries || c.Industries),

    Website: c.website || "",
    Crunchbase: c.organizationNameUrl || c["Organization Name URL"] || "",

    Founded: c.foundedDate || c["Founded Date"] || null,
    "Last Funding Date": c.lastFundingDate || c["Last Funding Date"] || null,
    "Funding Month": getFundingMonth(c.lastFundingDate || c["Last Funding Date"]),
    "Funding Quarter": getFundingQuarter(
      c.lastFundingDate || c["Last Funding Date"]
    ),
    "Funding Year": getFundingYear(c.lastFundingDate || c["Last Funding Date"]),

    "Headquarters Location": normalizeCountry(
      c.headquartersLocation || c["Headquarters Location"]
    ),
    "Crunchbase Rank": rankNumber,
    Employees: c.numberOfEmployees || c.Employees || "Unknown",

    "Lead Type": leadType,
    "Original Lead Type": leadType,

    Reasoning: reasoning || "No justification provided",
    "Investment Stage": normalizeFundingStage(
      c.lastFundingType || c["Investment Stage"]
    ),
    "Estimated Revenue Range":
      c.estimatedRevenueRange || c["Estimated Revenue Range"] || "",
    "Investor Type": c.investorType || c["Investor Type"] || "",
    "Total Funding Amount":
      c.totalFundingAmount || c["Total Funding Amount"] || "",
    "Top 5 Investors": c.top5Investors || c["Top 5 Investors"] || "",
    "Lead Investors": c.leadInvestors || c["Lead Investors"] || "",
    "New Addition": isNewAddition,
    Version: versionNumber,
    "Updated Company": isUpdatedCompany,
    "Update Category": updateCategory,
    "Update Categories": updateCategories,
    "Updated List Entry": updateCategories.length > 0,
    "Prospect Change Type": prospectChangeType,
    "Crunchbase Matching Name":
      c.crunchbaseMatchingName || c["Crunchbase Matching Name"] || "",
    "GlobalData Matching Name":
      c.globaldataMatchingName || c["GlobalData Matching Name"] || "",
    "Salesforce Client Matching Name": salesforceClientMatchingName,
    "Client Lifecycle Status": clientLifecycleStatus,
    contacted: Boolean(c.contacted ?? c.Contacted ?? false),
    Contacted: Boolean(c.contacted ?? c.Contacted ?? false),
    peopleRoleBuckets:
      c.peopleRoleBuckets || c["People Role Buckets"] || [],
    "People Role Buckets":
      c.peopleRoleBuckets || c["People Role Buckets"] || [],
    hasPeopleData:
      typeof c.hasPeopleData === "boolean"
        ? c.hasPeopleData
        : Boolean(c["Has People Data"]),
    "Has People Data":
      typeof c.hasPeopleData === "boolean"
        ? c.hasPeopleData
        : Boolean(c["Has People Data"]),
    "Manual Override": hasManualClassification,
    "Lead Reclassified": leadReclassified,
    Archived: Boolean(c.archived ?? c.Archived ?? false),
    "Classification Type": classificationType,
    "Classification Version":
      typeof c.classificationVersion === "number"
        ? c.classificationVersion
        : typeof c["Classification Version"] === "number"
        ? c["Classification Version"]
        : parseInt(
            String(c.classificationVersion ?? c["Classification Version"] ?? "0"),
            10
          ) || 0,

    original: c
  };
}

function applyFeedbackOverrides(company, feedbackEntry) {
  return {
    ...company,
    "Draft Comment": feedbackEntry?.comment || "",
    "Previous Lead Type": feedbackEntry?.previousLeadType || null,
    "Manual Override": Boolean(company["Manual Override"]),
  };
}

function getLeadTypeLabel(type) {
  if (type === "ACCEPT") return "Lead";
  if (type === "NOT ACCEPT") return "Discard";
  return "Unsure";
}

function getCompanyAgeBucket(foundedValue) {
  const foundedYear = getFundingYear(foundedValue);
  if (!foundedYear) return "";

  const age = new Date().getFullYear() - Number(foundedYear);
  if (Number.isNaN(age) || age < 0) return "";
  if (age < 3) return "0-3 yrs";
  if (age < 6) return "3-6 yrs";
  if (age < 10) return "6-10 yrs";
  return "10+ yrs";
}

function hasCriteriaValue(value) {
  const normalized = String(value || "").trim();
  return normalized !== "" && normalized.toLowerCase() !== "unknown";
}

function buildCrunchbaseCriteriaRows(companies, valueGetter, formatter = (value) => value) {
  const counts = new Map();

  (companies || []).forEach((company) => {
    const rawValues = valueGetter(company);
    const values = Array.isArray(rawValues) ? rawValues : [rawValues];

    values.forEach((value) => {
      if (!hasCriteriaValue(value)) return;
      const label = formatter(value);
      if (!hasCriteriaValue(label)) return;
      counts.set(label, (counts.get(label) || 0) + 1);
    });
  });

  return buildCriteriaRows(
    Array.from(counts.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((left, right) => {
        if (left.count !== right.count) return right.count - left.count;
        return left.label.localeCompare(right.label, "en-GB", { sensitivity: "base" });
      })
  );
}

function formatIsoDateForDisplay(isoDate) {
  if (!normalizeIsoDateParam(isoDate)) return isoDate || "";

  const date = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return isoDate;

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function App() {
  const theme = useTheme();
  const useCompactRightPanelTabs = useMediaQuery(theme.breakpoints.down("sm"));
  const initialQueryConfig = useMemo(
    () =>
      parseInitialQueryConfig(window.location.search, {
        ignoreGlobaldataDataset: isReloadNavigation(),
      }),
    []
  );
  const initialQueryConfigAppliedRef = useRef(false);
  const storedCrunchbaseWorkspaceConfigRef = useRef(loadStoredCrunchbaseWorkspaceConfig());

  // -------------------------------
  // AUTH STATE
  // -------------------------------
  const [authSession, setAuthSession] = useState(() => loadStoredAuthSession());
  const [authReady, setAuthReady] = useState(false);
  const [authFlow, setAuthFlow] = useState(() => loadAuthFlowFromSearch(window.location.search));
  const authenticated = Boolean(authSession?.token && authSession?.user);
  const currentUser = authSession?.user || null;
  const currentUserEmail = normalizeEmail(currentUser?.email);
  const canManageAssignments =
    isAdminUser(currentUser) || isBdAdminUser(currentUser);
  const canImportSalesforce =
    authenticated && DATA_SOURCE === "api" && canManageAssignments;
  const userConfigurationApiPath = "/api/user-configurations/me";

  const updateAuthFlow = useCallback((nextAuthFlow, options = {}) => {
    setAuthFlow(nextAuthFlow);
    syncAuthFlowInLocation(nextAuthFlow, options);
  }, []);

  // -------------------------------
  // MAIN STATE
  // -------------------------------
  const [companies, setCompanies] = useState([]);
  const [feedbackData, setFeedbackData] = useState({});
  const [loadedFeedback, setLoadedFeedback] = useState(false);

  const [workspaceDataset, setWorkspaceDataset] = useState(() =>
    initialQueryConfig?.workspaceDataset === "globaldata" ||
    initialQueryConfig?.workspaceDataset === WORKSPACE_DATASET_COMPANYHOUSE
      ? initialQueryConfig.workspaceDataset
      : "crunchbase"
  );
  const [currentTab, setCurrentTab] = useState("OVERVIEW");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGraphCompanyNames, setSelectedGraphCompanyNames] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState([]);
  const [selectedStage, setSelectedStage] = useState([]);
  const [selectedUpdateCategory, setSelectedUpdateCategory] = useState([]);
  const [selectedEstimatedRevenueRange, setSelectedEstimatedRevenueRange] = useState([]);
  const [selectedCompanyAge, setSelectedCompanyAge] = useState([]);
  const [selectedPeopleRoles, setSelectedPeopleRoles] = useState([]);
  const [selectedInvestorType, setSelectedInvestorType] = useState([]);
  const [selectedFundingPeriod, setSelectedFundingPeriod] = useState([]);
  const [selectedFundingYear, setSelectedFundingYear] = useState([]);
  const [selectedLeadStatus, setSelectedLeadStatus] = useState([]);
  const [selectedManualChange, setSelectedManualChange] = useState("");
  const [matchedState, setMatchedState] = useState("all");
  const [clientState, setClientState] = useState("all");
  const [selectedCrunchbaseExportFields, setSelectedCrunchbaseExportFields] = useState(
    DEFAULT_CRUNCHBASE_EXPORT_FIELDS
  );
  const [sortBy, setSortBy] = useState("");
  const [assignmentView, setAssignmentView] = useState("MY_LEADS");
  const [rightPanelView, setRightPanelView] = useState("visualisations");
  const [workspaceDatasetMenuAnchor, setWorkspaceDatasetMenuAnchor] = useState(null);
  const [savedSearches, setSavedSearches] = useState([]);
  const [savedSearchName, setSavedSearchName] = useState("");
  const [selectedSavedSearch, setSelectedSavedSearch] = useState("");
  const [savedSearchesBusy, setSavedSearchesBusy] = useState(false);
  const [userConfiguration, setUserConfiguration] = useState(null);
  const [updateWindowConfig, setUpdateWindowConfig] = useState(null);
  const [updatedWindowFrom, setUpdatedWindowFrom] = useState("");
  const [updatedWindowTo, setUpdatedWindowTo] = useState("");
  const [exportFileName, setExportFileName] = useState("company_feedback_export");
  const [exportFormat, setExportFormat] = useState(EXPORT_FORMAT_XLSX);
  const [companiesLoadError, setCompaniesLoadError] = useState("");
  const [companyHouseMatchesByCrunchbaseKey, setCompanyHouseMatchesByCrunchbaseKey] = useState(
    {}
  );
  const [assignmentCanonicalNameLookup, setAssignmentCanonicalNameLookup] = useState({});
  const [assignmentDatasetNameLookup, setAssignmentDatasetNameLookup] = useState({});
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [, setAssignmentsError] = useState("");
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignableUsersLoading, setAssignableUsersLoading] = useState(false);
  const [commentNotifications, setCommentNotifications] = useState({
    unreadCount: 0,
    lastSeenAt: null,
    evaluatedAt: null,
    items: [],
  });
  const [commentNotificationsLoading, setCommentNotificationsLoading] = useState(false);
  const [salesforceImportRefreshToken, setSalesforceImportRefreshToken] = useState(0);

  const [visibleCount, setVisibleCount] = useState(20);
  const [overrideNotice, setOverrideNotice] = useState("");

  const assignmentsByCompany = useMemo(
    () =>
      mapAssignmentsByCompany(
        assignments.map((assignment) => ({
          ...assignment,
          companyName: resolveCanonicalAssignmentCompanyName(
            assignment.companyName,
            assignmentCanonicalNameLookup
          ),
        }))
      ),
    [assignmentCanonicalNameLookup, assignments]
  );

  const getAssignmentKey = useCallback(
    (companyName) =>
      normalizeAssignmentCompanyKey(
        resolveCanonicalAssignmentCompanyName(companyName, assignmentCanonicalNameLookup)
      ),
    [assignmentCanonicalNameLookup]
  );

  const getAssignmentForCompanyName = useCallback(
    (companyName) => assignmentsByCompany[getAssignmentKey(companyName)] || null,
    [assignmentsByCompany, getAssignmentKey]
  );

  const handleCrunchbaseContactedChange = useCallback((companyName, contacted) => {
    const normalizedCompanyName = String(companyName || "").trim().toLowerCase();
    if (!normalizedCompanyName) {
      return;
    }

    setCompanies((currentCompanies) =>
      currentCompanies.map((company) =>
        String(company["Organization Name"] || "").trim().toLowerCase() === normalizedCompanyName
          ? {
              ...company,
              contacted,
              Contacted: contacted,
            }
          : company
      )
    );
  }, []);

  const visibleAssignments = useMemo(
    () =>
      assignmentView === "ALL" && canManageAssignments
        ? assignments
        : assignments.filter((assignment) => assignment.assignedTo === currentUserEmail),
    [assignmentView, assignments, canManageAssignments, currentUserEmail]
  );

  const resolveAssignedCompanyNamesForDataset = useCallback(
    (dataset) => {
      const names = [];
      const seen = new Set();

      visibleAssignments.forEach((assignment) => {
        resolveAssignmentNamesForDataset(
          assignment.companyName,
          assignmentDatasetNameLookup,
          dataset
        ).forEach((name) => {
          const normalizedKey = normalizeAssignmentCompanyKey(name);
          if (!normalizedKey || seen.has(normalizedKey)) {
            return;
          }
          seen.add(normalizedKey);
          names.push(name);
        });
      });

      return names;
    },
    [assignmentDatasetNameLookup, visibleAssignments]
  );

  const globaldataAssignedCompanyNames = useMemo(
    () => resolveAssignedCompanyNamesForDataset("globaldata"),
    [resolveAssignedCompanyNamesForDataset]
  );

  const companyHouseAssignedCompanyNames = useMemo(
    () => resolveAssignedCompanyNamesForDataset("companyhouse"),
    [resolveAssignedCompanyNamesForDataset]
  );

  useEffect(() => {
    persistWorkspaceDataset(workspaceDataset);
  }, [workspaceDataset]);

  const handleClassificationSummarySelect = useCallback((name) => {
    if (name === "Leads") {
      setCurrentTab("ACCEPT");
      setVisibleCount(20);
      return;
    }

    if (name === "Discard") {
      setCurrentTab("NOT ACCEPT");
      setVisibleCount(20);
      return;
    }

    if (name === "Unsure") {
      setCurrentTab("FUZZY");
      setVisibleCount(20);
    }
  }, []);
  const handleVisualisationFilterSelect = useCallback((filterType, value) => {
    if (!value) return;

    if (filterType === "industry") {
      setSelectedIndustry([value]);
      setVisibleCount(20);
      return;
    }

    if (filterType === "country") {
      setSelectedCountry([value]);
      setVisibleCount(20);
      return;
    }

    if (filterType === "stage") {
      setSelectedStage([value]);
      setVisibleCount(20);
      return;
    }

    if (filterType === "companyAge") {
      setSelectedCompanyAge([value]);
      setVisibleCount(20);
      return;
    }

    if (filterType === "fundingPeriod") {
      setSelectedFundingPeriod([value]);
      setVisibleCount(20);
    }
  }, []);

  const openWorkspaceDatasetMenu = (event) => {
    setWorkspaceDatasetMenuAnchor(event.currentTarget);
  };

  const closeWorkspaceDatasetMenu = () => {
    setWorkspaceDatasetMenuAnchor(null);
  };

  const fetchApiJson = useCallback(async (path, options = {}) => {
    const { allowNotFound = false, method = "GET", body } = options;
    const headers = {};

    if (body !== undefined) {
      headers["Content-Type"] = "application/json";
    }

    if (authSession?.token) {
      headers.Authorization = `Bearer ${authSession.token}`;
    }

    const response = await fetch(buildApiUrl(path), {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (allowNotFound && response.status === 404) {
      return null;
    }

    return parseResponseJson(response, `Failed to ${method} ${path}: ${response.status}`);
  }, [authSession?.token]);

  const getApiJson = useCallback((path, options) => fetchApiJson(path, options), [fetchApiJson]);
  const postJson = useCallback(
    (path, body) => fetchApiJson(path, { method: "POST", body }),
    [fetchApiJson]
  );
  const postApiFile = useCallback(
    async (path, body, fallbackFileName = "export.xlsx") => {
      const headers = {
        "Content-Type": "application/json",
      };

      if (authSession?.token) {
        headers.Authorization = `Bearer ${authSession.token}`;
      }

      const response = await fetch(buildApiUrl(path), {
        method: "POST",
        headers,
        body: JSON.stringify(body ?? {}),
      });

      if (!response.ok) {
        await parseResponseJson(response, `Failed to POST ${path}: ${response.status}`);
      }

      const contentDisposition = response.headers.get("content-disposition") || "";
      const fileNameMatch =
        contentDisposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        contentDisposition.match(/filename="?([^";]+)"?/i);
      const fileName = fileNameMatch?.[1]
        ? decodeURIComponent(fileNameMatch[1])
        : fallbackFileName;

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [authSession?.token]
  );
  const patchJson = useCallback(
    (path, body) => fetchApiJson(path, { method: "PATCH", body }),
    [fetchApiJson]
  );
  const deleteJson = useCallback(
    (path, body) => fetchApiJson(path, { method: "DELETE", body }),
    [fetchApiJson]
  );

  const loadCommentNotifications = useCallback(async () => {
    if (!authenticated || !canManageAssignments || DATA_SOURCE !== "api") {
      setCommentNotifications({
        unreadCount: 0,
        lastSeenAt: null,
        evaluatedAt: null,
        items: [],
      });
      return;
    }

    try {
      setCommentNotificationsLoading(true);
      const payload = await getApiJson("/api/leads/comment-notifications");
      setCommentNotifications(normalizeCommentNotificationResponse(payload));
    } catch (error) {
      console.error("Unable to load comment notifications:", error);
    } finally {
      setCommentNotificationsLoading(false);
    }
  }, [authenticated, canManageAssignments, getApiJson]);

  const markCommentNotificationsSeen = useCallback(async () => {
    if (
      !authenticated ||
      !canManageAssignments ||
      DATA_SOURCE !== "api" ||
      !commentNotifications.evaluatedAt
    ) {
      return;
    }

    try {
      const payload = await postJson("/api/leads/comment-notifications/mark-seen", {
        seenAt: commentNotifications.evaluatedAt,
      });
      setCommentNotifications((current) => ({
        ...current,
        unreadCount: 0,
        lastSeenAt: payload?.lastSeenAt || commentNotifications.evaluatedAt,
      }));
    } catch (error) {
      console.error("Unable to mark comment notifications as seen:", error);
    }
  }, [
    authenticated,
    canManageAssignments,
    commentNotifications.evaluatedAt,
    postJson,
  ]);

  const loadAssignments = useCallback(async () => {
    if (!authenticated) {
      setAssignments([]);
      setAssignmentsError("");
      return;
    }

    setAssignmentsLoading(true);
    setAssignmentsError("");

    try {
      const endpoint = canManageAssignments
        ? "/api/lead-management/leads"
        : "/api/lead-management/leads/mine";
      const payload = await fetchApiJson(endpoint);
      const normalizedAssignments = Array.isArray(payload)
        ? payload.map(normalizeLeadAssignment).filter(Boolean)
        : [];

      setAssignments(normalizedAssignments);
    } catch (error) {
      console.error("Unable to load assignments:", error);
      setAssignments([]);
      setAssignmentsError(error.message || "Unable to load assignments.");
    } finally {
      setAssignmentsLoading(false);
    }
  }, [authenticated, canManageAssignments, fetchApiJson]);

  const loadAssignableUsers = useCallback(async () => {
    if (!authenticated || !canManageAssignments) {
      setAssignableUsers([]);
      return;
    }

    setAssignableUsersLoading(true);

    try {
      const payload = await fetchApiJson("/api/user-configurations");
      const emails = Array.isArray(payload)
        ? payload
            .map((entry) => normalizeEmail(entry?.email))
            .filter(Boolean)
        : [];

      setAssignableUsers(Array.from(new Set(emails)).sort());
    } catch (error) {
      console.warn("Falling back to assignment-derived assignee options:", error);
      setAssignableUsers([]);
    } finally {
      setAssignableUsersLoading(false);
    }
  }, [authenticated, canManageAssignments, fetchApiJson]);

  const buildCompaniesApiPath = useCallback(() => {
    const query = new URLSearchParams({
      from: updatedWindowFrom,
      to: addDaysToIsoDate(updatedWindowTo, 1),
    }).toString();
    const separator = COMPANIES_API_PATH.includes("?") ? "&" : "?";
    return `${COMPANIES_API_PATH}${separator}${query}`;
  }, [updatedWindowFrom, updatedWindowTo]);

  const effectiveTimelineMinDate = useMemo(
    () =>
      minIsoDate(
        updateWindowConfig?.minDate || "",
        updateWindowConfig?.selectedFrom || "",
        updatedWindowFrom
      ) || updateWindowConfig?.minDate || "",
    [
      updateWindowConfig?.minDate,
      updateWindowConfig?.selectedFrom,
      updatedWindowFrom,
    ]
  );

  const effectiveTimelineMaxDate = useMemo(
    () =>
      maxIsoDate(
        updateWindowConfig?.maxDate || "",
        updateWindowConfig?.selectedTo || "",
        updatedWindowTo
      ) || updateWindowConfig?.maxDate || "",
    [
      updateWindowConfig?.maxDate,
      updateWindowConfig?.selectedTo,
      updatedWindowTo,
    ]
  );

  useEffect(() => {
    let cancelled = false;

    const restoreSession = async () => {
      const storedSession = loadStoredAuthSession();

      if (!storedSession?.token) {
        if (!cancelled) {
          clearStoredAuthSession();
          setAuthSession(null);
          setAuthReady(true);
        }
        return;
      }

      try {
        const response = await fetch(buildAuthApiUrl("/validateToken"), {
          headers: {
            Authorization: `Bearer ${storedSession.token}`,
          },
        });
        const validation = await parseResponseJson(response, "Unable to validate session.");

        if (!validation?.valid || !validation?.user) {
          throw new Error("Your session is no longer valid. Please log in again.");
        }

        const nextSession = {
          token: storedSession.token,
          user: validation.user,
        };

        if (!cancelled) {
          updateAuthFlow({ mode: AUTH_FLOW_LOGIN, token: "" }, { replace: true });
          persistAuthSession(nextSession);
          setAuthSession(nextSession);
          setAuthReady(true);
        }
      } catch (err) {
        console.error("Session restore error:", err);
        if (!cancelled) {
          clearStoredAuthSession();
          setAuthSession(null);
          setAuthReady(true);
        }
      }
    };

    restoreSession();

    return () => {
      cancelled = true;
    };
  }, [updateAuthFlow]);

  useEffect(() => {
    const handlePopState = () => {
      setAuthFlow(loadAuthFlowFromSearch(window.location.search));
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    if (!authenticated) {
      setAssignments([]);
      setAssignableUsers([]);
      setAssignmentView("MY_LEADS");
      return;
    }

    loadAssignments();
    loadAssignableUsers();
  }, [authenticated, loadAssignableUsers, loadAssignments]);

  useEffect(() => {
    if (canManageAssignments) return;
    setAssignmentView("MY_LEADS");
  }, [canManageAssignments]);

  useEffect(() => {
    if (!canManageAssignments || assignableUsers.length > 0) return;

    const fallbackEmails = Array.from(
      new Set(
        [
          currentUserEmail,
          ...assignments.map((assignment) => assignment.assignedTo),
          ...assignments.map((assignment) => assignment.assignedBy),
        ].filter(Boolean)
      )
    ).sort();

    if (fallbackEmails.length > 0) {
      setAssignableUsers(fallbackEmails);
    }
  }, [assignableUsers.length, assignments, canManageAssignments, currentUserEmail]);

  const loadCompanyMatcherMappings = useCallback(async () => {
    if (DATA_SOURCE !== "api" || !authenticated) {
      setCompanyHouseMatchesByCrunchbaseKey({});
      setAssignmentCanonicalNameLookup({});
      setAssignmentDatasetNameLookup({});
      return;
    }

    try {
      const response = await getApiJson("/api/company-matcher/matched-names");
      const nextLookup = {};
      (response || []).forEach((row) => {
        const crunchbaseKey = buildCrossWorkspaceSearchTerm(row.cbName);
        const companyHouseName = String(row.companyHouseName || "").trim();
        if (!crunchbaseKey || !companyHouseName) return;
        if (!nextLookup[crunchbaseKey]) {
          nextLookup[crunchbaseKey] = [];
        }
        if (!nextLookup[crunchbaseKey].includes(companyHouseName)) {
          nextLookup[crunchbaseKey].push(companyHouseName);
        }
      });

      setCompanyHouseMatchesByCrunchbaseKey(nextLookup);
      setAssignmentCanonicalNameLookup(buildAssignmentCanonicalNameLookup(response || []));
      setAssignmentDatasetNameLookup(buildAssignmentDatasetNameLookup(response || []));
    } catch (error) {
      console.error("Unable to load Company House matcher mappings:", error);
      setAssignmentCanonicalNameLookup({});
      setAssignmentDatasetNameLookup({});
    }
  }, [authenticated, getApiJson]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (cancelled) {
        return;
      }
      await loadCompanyMatcherMappings();
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [loadCompanyMatcherMappings]);

  const loadCompanies = useCallback(async () => {
    try {
      setCompaniesLoadError("");

      if (DATA_SOURCE === "api") {
        if (!updatedWindowFrom || !updatedWindowTo) {
          setCompanies([]);
          return;
        }

        const currentRaw = await getApiJson(buildCompaniesApiPath());
        const normalized = currentRaw.map((c) => normalizeCompany(c));

        setCompanies(normalized);
        return;
      }

      const currentRaw = await fetchJson("/data/allDetail_pharmaron_v1.json");
      const normalized = currentRaw.map((c) => normalizeCompany(c));

      setCompanies(normalized);
    } catch (err) {
      console.error("Company data load error:", err);
      setCompaniesLoadError(err.message || "Unable to load companies.");
    }
  }, [buildCompaniesApiPath, getApiJson, updatedWindowFrom, updatedWindowTo]);

  const importSalesforceWorkbook = useCallback(
    async (file) => {
      if (!canImportSalesforce) {
        throw new Error("You do not have permission to import Salesforce workbooks.");
      }
      if (!file) {
        throw new Error("Select a Salesforce workbook before importing.");
      }

      const formData = new FormData();
      formData.append("file", file);

      const headers = {};
      if (authSession?.token) {
        headers.Authorization = `Bearer ${authSession.token}`;
      }

      const response = await fetch(buildApiUrl("/api/salesforce/import"), {
        method: "POST",
        headers,
        body: formData,
      });
      const payload = await parseResponseJson(
        response,
        `Failed to POST /api/salesforce/import: ${response.status}`
      );

      await loadCompanyMatcherMappings();
      await loadCompanies();
      setSalesforceImportRefreshToken((current) => current + 1);
      return payload;
    },
    [
      authSession?.token,
      canImportSalesforce,
      loadCompanies,
      loadCompanyMatcherMappings,
    ]
  );

  const companiesWithCrossDatasetMatches = useMemo(
    () =>
      companies.map((company) => {
        const companyHouseMatches =
          companyHouseMatchesByCrunchbaseKey[
            buildCrossWorkspaceSearchTerm(company["Organization Name"])
          ] || [];

        return {
          ...company,
          "Company House Matching Name": companyHouseMatches[0] || "",
        };
      }),
    [companies, companyHouseMatchesByCrunchbaseKey]
  );

  // -------------------------------------------------------
  // LOAD FEEDBACK FIRST
  // -------------------------------------------------------
  useEffect(() => {
    const initializeSavedSearches = async () => {
      if (!authenticated) {
        setLoadedFeedback(true);
        return;
      }

      if (DATA_SOURCE !== "api") {
        const savedSearchesRaw = localStorage.getItem(SAVED_SEARCHES_KEY);
        if (savedSearchesRaw) {
          try {
            const parsed = JSON.parse(savedSearchesRaw);
            if (Array.isArray(parsed)) {
              setSavedSearches(parsed.map(normalizeSavedSearchEntry).filter(Boolean));
            }
          } catch (err) {
            console.error("Error parsing saved searches:", err);
          }
        }
      }

      if (DATA_SOURCE === "api") {
        try {
          const nextUserConfiguration = await fetchApiJson(userConfigurationApiPath, {
            allowNotFound: true,
          });
          if (nextUserConfiguration) {
            setUserConfiguration(nextUserConfiguration);
            setSavedSearches(parseSavedSearchesXml(nextUserConfiguration.savedFilterXml));
          } else {
            setUserConfiguration(null);
          }

          const nextUpdateWindow = await fetchApiJson(UPDATE_WINDOW_API_PATH);
          setUpdateWindowConfig(nextUpdateWindow);
          setUpdatedWindowFrom(nextUpdateWindow.selectedFrom || "");
          setUpdatedWindowTo(nextUpdateWindow.selectedTo || "");
        } catch (err) {
          console.error("Error loading saved searches from backend:", err);
          setCompaniesLoadError(err.message || "Unable to load the updated window.");
        }
      }

      setLoadedFeedback(true);
    };

    initializeSavedSearches();
  }, [authenticated, userConfigurationApiPath, fetchApiJson]);

  useEffect(() => {
    if (!authenticated || !canManageAssignments || DATA_SOURCE !== "api") {
      setCommentNotifications({
        unreadCount: 0,
        lastSeenAt: null,
        evaluatedAt: null,
        items: [],
      });
      return undefined;
    }

    loadCommentNotifications();
    const intervalId = window.setInterval(loadCommentNotifications, 60000);
    return () => window.clearInterval(intervalId);
  }, [authenticated, canManageAssignments, loadCommentNotifications]);

  useEffect(() => {
    if (
      !authenticated ||
      !loadedFeedback ||
      !updateWindowConfig ||
      initialQueryConfigAppliedRef.current
    ) {
      return;
    }

    const storedCrunchbaseConfig = storedCrunchbaseWorkspaceConfigRef.current || {};

    setWorkspaceDataset(initialQueryConfig?.workspaceDataset || "crunchbase");
    if (
      initialQueryConfig?.workspaceDataset === "globaldata" ||
      initialQueryConfig?.workspaceDataset === WORKSPACE_DATASET_COMPANYHOUSE
    ) {
      initialQueryConfigAppliedRef.current = true;
      return;
    }

    setCurrentTab(
      initialQueryConfig?.currentTab || storedCrunchbaseConfig.currentTab || "OVERVIEW"
    );
    setRightPanelView(
      initialQueryConfig?.rightPanelView || "visualisations"
    );
    setUpdatedWindowFrom(
      initialQueryConfig?.updatedWindowFrom ||
        storedCrunchbaseConfig.updatedWindowFrom ||
        updateWindowConfig.selectedFrom ||
        ""
    );
    setUpdatedWindowTo(
      initialQueryConfig?.updatedWindowTo ||
        storedCrunchbaseConfig.updatedWindowTo ||
        updateWindowConfig.selectedTo ||
        ""
    );
    setSearchTerm(initialQueryConfig?.searchTerm || storedCrunchbaseConfig.searchTerm || "");
    setSelectedGraphCompanyNames(
      normalizeSelection(storedCrunchbaseConfig.selectedGraphCompanyNames)
    );
    setSelectedIndustry(normalizeSelection(storedCrunchbaseConfig.selectedIndustry));
    setSelectedCountry(normalizeSelection(storedCrunchbaseConfig.selectedCountry));
    setSelectedStage(normalizeSelection(storedCrunchbaseConfig.selectedStage));
    setSelectedUpdateCategory(
      normalizeSelection(
        initialQueryConfig?.selectedUpdateCategory ||
          storedCrunchbaseConfig.selectedUpdateCategory
      )
    );
    setSelectedEstimatedRevenueRange(
      normalizeSelection(storedCrunchbaseConfig.selectedEstimatedRevenueRange)
    );
    setSelectedCompanyAge(normalizeSelection(storedCrunchbaseConfig.selectedCompanyAge));
    setSelectedPeopleRoles(normalizeSelection(storedCrunchbaseConfig.selectedPeopleRoles));
    setSelectedInvestorType(normalizeSelection(storedCrunchbaseConfig.selectedInvestorType));
    setSelectedFundingPeriod(normalizeSelection(storedCrunchbaseConfig.selectedFundingPeriod));
    setSelectedFundingYear(normalizeSelection(storedCrunchbaseConfig.selectedFundingYear));
    setSelectedLeadStatus(
      normalizeSelection(
        initialQueryConfig?.selectedLeadStatus || storedCrunchbaseConfig.selectedLeadStatus
      )
    );
    setSelectedManualChange(storedCrunchbaseConfig.selectedManualChange || "");
    setMatchedState(initialQueryConfig?.matchedState || storedCrunchbaseConfig.matchedState || "all");
    setClientState(initialQueryConfig?.clientState || storedCrunchbaseConfig.clientState || "all");
    setSortBy(storedCrunchbaseConfig.sortBy || "");
    setSelectedSavedSearch("");
    setVisibleCount(20);
    initialQueryConfigAppliedRef.current = true;
  }, [authenticated, initialQueryConfig, loadedFeedback, updateWindowConfig]);

  useEffect(() => {
    if (DATA_SOURCE !== "api" && loadedFeedback) {
      localStorage.setItem(SAVED_SEARCHES_KEY, JSON.stringify(savedSearches));
    }
  }, [savedSearches, loadedFeedback]);

  useEffect(() => {
    if (!authenticated || !loadedFeedback || workspaceDataset !== "crunchbase") {
      return;
    }

    const nextCrunchbaseWorkspaceConfig = {
      currentTab,
      updatedWindowFrom,
      updatedWindowTo,
      searchTerm,
      selectedGraphCompanyNames,
      selectedIndustry,
      selectedCountry,
      selectedStage,
      selectedUpdateCategory,
      selectedEstimatedRevenueRange,
      selectedCompanyAge,
      selectedPeopleRoles,
      selectedInvestorType,
      selectedFundingPeriod,
      selectedFundingYear,
      selectedLeadStatus,
      selectedManualChange,
      matchedState,
      clientState,
      sortBy,
    };

    storedCrunchbaseWorkspaceConfigRef.current = nextCrunchbaseWorkspaceConfig;
    persistStoredCrunchbaseWorkspaceConfig(nextCrunchbaseWorkspaceConfig);
  }, [
    authenticated,
    clientState,
    currentTab,
    loadedFeedback,
    matchedState,
    searchTerm,
    selectedCompanyAge,
    selectedCountry,
    selectedEstimatedRevenueRange,
    selectedFundingPeriod,
    selectedFundingYear,
    selectedGraphCompanyNames,
    selectedIndustry,
    selectedInvestorType,
    selectedLeadStatus,
    selectedManualChange,
    selectedPeopleRoles,
    selectedStage,
    selectedUpdateCategory,
    sortBy,
    updatedWindowFrom,
    updatedWindowTo,
    workspaceDataset,
  ]);

  // -------------------------------------------------------
  // SHARED BUTTON STYLE
  // -------------------------------------------------------
  const actionButtonStyle = {
    fontWeight: 600,
    borderWidth: 2,
    textTransform: "none",
    px: 1.75,
    py: 0.5,
    height: 34,
    fontSize: "0.82rem",
    "&:hover": { borderWidth: 2 }
  };

  // -------------------------------------------------------
  // INDUSTRY BUCKET MAP
  // -------------------------------------------------------
  // -------------------------------------------------------
  // LOAD JSON AFTER FEEDBACK
  // -------------------------------------------------------
  useEffect(() => {
    if (!loadedFeedback || !authenticated) return;
    if (initialQueryConfig && !initialQueryConfigAppliedRef.current) return;
    if (DATA_SOURCE === "api" && (!updatedWindowFrom || !updatedWindowTo)) return;

    loadCompanies();
  }, [
    authenticated,
    initialQueryConfig,
    loadCompanies,
    loadedFeedback,
    updatedWindowFrom,
    updatedWindowTo,
  ]);

  const networkFilters = useMemo(
    () => ({
      currentTab,
      searchTerm,
      selectedGraphCompanyNames,
      updatedWindowFrom,
      updatedWindowToExclusive: updatedWindowTo ? addDaysToIsoDate(updatedWindowTo, 1) : "",
      selectedIndustry,
      selectedCountry,
      selectedStage,
      selectedCompanyAge,
      matchedState,
      clientState,
      selectedUpdateCategory,
      selectedEstimatedRevenueRange,
      selectedInvestorType,
      selectedFundingPeriod,
      selectedFundingYear,
      selectedLeadStatus,
      selectedManualChange,
      assignmentView,
    }),
    [
      currentTab,
      searchTerm,
      selectedGraphCompanyNames,
      updatedWindowFrom,
      updatedWindowTo,
      selectedIndustry,
      selectedCountry,
      selectedStage,
      selectedCompanyAge,
      matchedState,
      clientState,
      selectedUpdateCategory,
      selectedEstimatedRevenueRange,
      selectedInvestorType,
      selectedFundingPeriod,
      selectedFundingYear,
      selectedLeadStatus,
      selectedManualChange,
      assignmentView,
    ]
  );

  // -------------------------------------------------------
  // LOGIN GATE
  // -------------------------------------------------------
  const handleLogin = async ({ email, password }) => {
    const response = await fetch(buildAuthApiUrl("/login"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
        password,
      }),
    });

    const payload = await parseResponseJson(
      response,
      "Unable to log in with those credentials."
    );

    if (!payload?.access_token || !payload?.user) {
      throw new Error("Login succeeded but the auth response was incomplete.");
    }

    const nextSession = {
      token: payload.access_token,
      user: payload.user,
    };

    updateAuthFlow({ mode: AUTH_FLOW_LOGIN, token: "" }, { replace: true });
    persistAuthSession(nextSession);
    setAuthSession(nextSession);
    setLoadedFeedback(false);
  };

  const handleForgotPassword = async ({ email }) => {
    const response = await fetch(buildAuthApiUrl("/forgotPassword"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
      }),
    });

    return parseResponseJson(
      response,
      "Unable to send the password reset email."
    );
  };

  const handleResetPassword = async ({ token, newPassword }) => {
    const response = await fetch(buildAuthApiUrl("/resetPassword"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        token,
        new_password: newPassword,
      }),
    });

    return parseResponseJson(response, "Unable to reset the password.");
  };

  const handleLogout = async () => {
    try {
      if (commentNotifications.unreadCount > 0) {
        await markCommentNotificationsSeen();
      }
    } catch (error) {
      console.error("Unable to persist comment notifications on logout:", error);
    } finally {
      initialQueryConfigAppliedRef.current = false;
      clearStoredAuthSession();
      setAuthSession(null);
      updateAuthFlow({ mode: AUTH_FLOW_LOGIN, token: "" }, { replace: true });
      setWorkspaceDataset("crunchbase");
      setCompanies([]);
      setFeedbackData({});
      setLoadedFeedback(false);
      setSavedSearches([]);
      setSelectedSavedSearch("");
      setUpdateWindowConfig(null);
      setUpdatedWindowFrom("");
      setUpdatedWindowTo("");
      setCompaniesLoadError("");
      setAssignments([]);
      setAssignmentsError("");
      setAssignableUsers([]);
      setAssignmentView("MY_LEADS");
      setSelectedGraphCompanyNames([]);
      setCommentNotifications({
        unreadCount: 0,
        lastSeenAt: null,
        evaluatedAt: null,
        items: [],
      });
    }
  };

  const handleChangePassword = async ({ currentPassword, newPassword }) => {
    if (!authSession?.token) {
      throw new Error("You must be logged in to change your password.");
    }

    const response = await fetch(buildAuthApiUrl("/changePasswordForCurrentUser"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authSession.token}`,
      },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });

    return parseResponseJson(response, "Unable to update password.");
  };

  const handleSaveUserConfiguration = async ({
    displayInitials,
    notificationFrequency,
    userProfileImage,
  }) => {
    const payload = {
      displayInitials: displayInitials?.trim() ? displayInitials.trim() : null,
      notificationFrequency,
      userProfileImage: userProfileImage?.trim() ? userProfileImage.trim() : null,
    };

    const updatedConfiguration = await patchJson(userConfigurationApiPath, payload);
    setUserConfiguration(updatedConfiguration);
    return updatedConfiguration;
  };

  if (!authReady) {
    return <div>Restoring session...</div>;
  }

  if (!authenticated) {
    return (
      <Login
        mode={authFlow.mode}
        resetToken={authFlow.token}
        onLogin={handleLogin}
        onForgotPassword={handleForgotPassword}
        onResetPassword={handleResetPassword}
        onNavigateToForgotPassword={() =>
          updateAuthFlow({ mode: AUTH_FLOW_FORGOT_PASSWORD, token: "" })
        }
        onNavigateToLogin={() =>
          updateAuthFlow({ mode: AUTH_FLOW_LOGIN, token: "" }, { replace: true })
        }
      />
    );
  }

  // -------------------------------------------------------
  // RESET FILTERS
  // -------------------------------------------------------
  const resetAll = () => {
    clearStoredWorkspaceCompanySearchState();
    const resetFrom = updateWindowConfig?.selectedFrom || "";
    const resetTo = updateWindowConfig?.selectedTo || "";
    const clearedCrunchbaseConfig = buildClearedCrunchbaseWorkspaceConfig({
      currentTab,
      updatedWindowFrom: resetFrom,
      updatedWindowTo: resetTo,
    });

    storedCrunchbaseWorkspaceConfigRef.current = clearedCrunchbaseConfig;
    persistStoredCrunchbaseWorkspaceConfig(clearedCrunchbaseConfig);
    setSearchTerm("");
    setSelectedGraphCompanyNames([]);
    setUpdatedWindowFrom(resetFrom);
    setUpdatedWindowTo(resetTo);
    setSelectedIndustry([]);
    setSelectedCountry([]);
    setSelectedStage([]);
    setSelectedUpdateCategory([]);
    setSelectedEstimatedRevenueRange([]);
    setSelectedCompanyAge([]);
    setSelectedPeopleRoles([]);
    setSelectedInvestorType([]);
    setSelectedFundingPeriod([]);
    setSelectedFundingYear([]);
    setSelectedLeadStatus([]);
    setSelectedManualChange("");
    setMatchedState("all");
    setClientState("all");
    setSortBy("");
    setSelectedSavedSearch("");
    setVisibleCount(20);

    if (typeof window !== "undefined") {
      window.history.replaceState({}, "", window.location.pathname);
    }
  };

  const handleHomeClick = () => {
    resetAll();
    setWorkspaceDataset("crunchbase");
    setCurrentTab("OVERVIEW");
    setRightPanelView("visualisations");
    setWorkspaceDatasetMenuAnchor(null);
    persistWorkspaceDataset("crunchbase");
    window.history.replaceState({}, "", window.location.pathname);
  };

  const applyGraphCompanyFilter = (companyNames) => {
    const seen = new Set();
    const nextNames = [];

    (companyNames || []).forEach((companyName) => {
      const trimmedName = String(companyName || "").trim();
      const key = normalizeKey(trimmedName);
      if (!key || seen.has(key)) return;
      seen.add(key);
      nextNames.push(trimmedName);
    });

    setSelectedGraphCompanyNames(nextNames);
    setVisibleCount(20);
  };

  const addManualGraphCompanyFilter = (rawCompanyName) => {
    const query = normalizeKey(rawCompanyName);
    if (!query) return false;

    const exactMatch = companiesWithOverrides.find(
      (company) => normalizeKey(company["Organization Name"]) === query
    );
    const fuzzyMatches = exactMatch
      ? []
      : companiesWithOverrides.filter((company) =>
          normalizeKey(company["Organization Name"]).includes(query)
        );
    const matchedCompany =
      exactMatch || (fuzzyMatches.length === 1 ? fuzzyMatches[0] : null);

    if (!matchedCompany) return false;

    const matchedName = matchedCompany["Organization Name"];
    setSelectedGraphCompanyNames((current) => {
      const seen = new Set();
      return [...current, matchedName].filter((companyName) => {
        const key = normalizeKey(companyName);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    });
    setSearchTerm("");
    setVisibleCount(20);
    return true;
  };

  const removeSelectedGraphCompanyName = (companyName) => {
    const keyToRemove = normalizeKey(companyName);
    setSelectedGraphCompanyNames((current) =>
      current.filter((entry) => normalizeKey(entry) !== keyToRemove)
    );
    setVisibleCount(20);
  };

  const navigateToWorkspace = (params) => {
    const query = params.toString();
    const nextUrl = query
      ? `${window.location.pathname}?${query}`
      : window.location.pathname;
    window.location.assign(nextUrl);
  };

  const openCrunchbaseCompanyMatch = (companyName) => {
    const normalizedSearchTerm = buildCrossWorkspaceSearchTerm(companyName);
    const storedCrunchbaseConfig =
      workspaceDataset === "crunchbase"
        ? buildSearchConfig()
        : storedCrunchbaseWorkspaceConfigRef.current || {};
    const nextFrom =
      storedCrunchbaseConfig.updatedWindowFrom ||
      updateWindowConfig?.selectedFrom ||
      updateWindowConfig?.minDate ||
      "";
    const nextTo =
      storedCrunchbaseConfig.updatedWindowTo ||
      updateWindowConfig?.selectedTo ||
      updateWindowConfig?.maxDate ||
      "";

    const params = new URLSearchParams({
      dataset: "crunchbase",
      tab: storedCrunchbaseConfig.currentTab || "OVERVIEW",
      view: "companies",
    });

    if (nextFrom) params.set("from", nextFrom);
    if (nextTo) params.set("to", nextTo);
    if (storedCrunchbaseConfig.matchedState && storedCrunchbaseConfig.matchedState !== "all") {
      params.set("matchedState", storedCrunchbaseConfig.matchedState);
    }
    if (storedCrunchbaseConfig.clientState && storedCrunchbaseConfig.clientState !== "all") {
      params.set("clientState", storedCrunchbaseConfig.clientState);
    }
    if (normalizedSearchTerm) params.set("search", normalizedSearchTerm);

    navigateToWorkspace(params);
  };

  const openGlobaldataCompanyMatch = (companyName) => {
    const normalizedSearchTerm = buildCrossWorkspaceSearchTerm(companyName);
    const params = new URLSearchParams({
      dataset: "globaldata",
      view: "companies",
    });

    if (normalizedSearchTerm) {
      params.set("search", normalizedSearchTerm);
    }

    navigateToWorkspace(params);
  };

  const openCompanyHouseCompanyMatch = (companyName) => {
    const normalizedSearchTerm = buildCrossWorkspaceSearchTerm(companyName);
    const params = new URLSearchParams({
      dataset: "companyhouse",
      view: "companies",
    });

    if (normalizedSearchTerm) {
      params.set("search", normalizedSearchTerm);
    }

    navigateToWorkspace(params);
  };

  const handleCommentNotificationSelect = (notification) => {
    if (!notification?.organizationName || !notification?.leadContext) {
      return;
    }

    if (notification.leadContext === "CRUNCHBASE") {
      openCrunchbaseCompanyMatch(notification.organizationName);
      return;
    }

    if (notification.leadContext === "GLOBALDATA") {
      openGlobaldataCompanyMatch(notification.organizationName);
      return;
    }

    if (notification.leadContext === "COMPANIES_HOUSE") {
      openCompanyHouseCompanyMatch(notification.organizationName);
    }
  };

  const switchWorkspaceDataset = (nextDataset) => {
    if (nextDataset === workspaceDataset) {
      closeWorkspaceDatasetMenu();
      return;
    }

    const defaultView =
      nextDataset === "crunchbase" || nextDataset === "globaldata" || nextDataset === "companyhouse"
        ? "visualisations"
        : rightPanelView || "visualisations";

    const params = new URLSearchParams({
      dataset: nextDataset,
      view: defaultView,
    });

    if (nextDataset === "crunchbase") {
      params.set("tab", currentTab || "OVERVIEW");
      if (updatedWindowFrom) params.set("from", updatedWindowFrom);
      if (updatedWindowTo) params.set("to", updatedWindowTo);
    }

    closeWorkspaceDatasetMenu();
    navigateToWorkspace(params);
  };

  const buildSearchConfig = () => ({
    currentTab,
    updatedWindowFrom,
    updatedWindowTo,
    searchTerm,
    selectedGraphCompanyNames,
    selectedIndustry,
    selectedCountry,
    selectedStage,
    selectedUpdateCategory,
    selectedEstimatedRevenueRange,
    selectedCompanyAge,
    selectedPeopleRoles,
    selectedInvestorType,
    selectedFundingPeriod,
    selectedFundingYear,
    selectedLeadStatus,
    selectedManualChange,
    matchedState,
    clientState,
    sortBy,
  });

  const crunchbaseSavedSearches = savedSearches.filter(
    (search) => getSavedSearchDataset(search.dataset) === SAVED_SEARCH_DATASET_CRUNCHBASE
  );

  const globaldataSavedSearches = savedSearches.filter(
    (search) => getSavedSearchDataset(search.dataset) === SAVED_SEARCH_DATASET_GLOBALDATA
  );

  const applySavedSearch = (config) => {
    setCurrentTab(config.currentTab || "OVERVIEW");
    setUpdatedWindowFrom(config.updatedWindowFrom || updateWindowConfig?.selectedFrom || "");
    setUpdatedWindowTo(config.updatedWindowTo || updateWindowConfig?.selectedTo || "");
    setSearchTerm(config.searchTerm || "");
    setSelectedGraphCompanyNames(normalizeSelection(config.selectedGraphCompanyNames));
    setSelectedIndustry(normalizeSelection(config.selectedIndustry));
    setSelectedCountry(normalizeSelection(config.selectedCountry));
    setSelectedStage(normalizeSelection(config.selectedStage));
    setSelectedUpdateCategory(normalizeSelection(config.selectedUpdateCategory));
    setSelectedEstimatedRevenueRange(
      normalizeSelection(config.selectedEstimatedRevenueRange)
    );
    setSelectedCompanyAge(normalizeSelection(config.selectedCompanyAge));
    setSelectedPeopleRoles(normalizeSelection(config.selectedPeopleRoles));
    setSelectedInvestorType(normalizeSelection(config.selectedInvestorType));
    setSelectedFundingPeriod(normalizeSelection(config.selectedFundingPeriod));
    setSelectedFundingYear(normalizeSelection(config.selectedFundingYear));
    setSelectedLeadStatus(normalizeSelection(config.selectedLeadStatus));
    setSelectedManualChange(config.selectedManualChange || "");
    setMatchedState(config.matchedState || "all");
    setClientState(config.clientState || "all");
    setSortBy(config.sortBy || "");
    setVisibleCount(20);
  };

  const persistSavedSearches = async (nextSavedSearches) => {
    if (DATA_SOURCE !== "api") {
      return;
    }

    const savedFilterXml =
      nextSavedSearches.length > 0
        ? serializeSavedSearchesToXml(
            nextSavedSearches.map(normalizeSavedSearchEntry).filter(Boolean)
          )
        : null;

    await patchJson(userConfigurationApiPath, {
      savedFilterXml,
    });
  };

  const saveCurrentSearch = async () => {
    const trimmedName = savedSearchName.trim();
    if (!trimmedName) return;

    const nextSavedSearches = upsertSavedSearch(
      savedSearches,
      SAVED_SEARCH_DATASET_CRUNCHBASE,
      trimmedName,
      buildSearchConfig()
    );

    try {
      setSavedSearchesBusy(true);
      await persistSavedSearches(nextSavedSearches);
      setSavedSearches(nextSavedSearches);
      setSelectedSavedSearch(trimmedName);
      setSavedSearchName("");
    } catch (err) {
      console.error("Error saving saved search:", err);
    } finally {
      setSavedSearchesBusy(false);
    }
  };

  const loadSavedSearch = (name) => {
    setSelectedSavedSearch(name);
    const match = crunchbaseSavedSearches.find((search) => search.name === name);
    if (match) {
      applySavedSearch(match.config);
    }
  };

  const deleteSavedSearch = async () => {
    if (!selectedSavedSearch) return;

    const nextSavedSearches = savedSearches.filter(
      (search) =>
        !(
          getSavedSearchDataset(search.dataset) === SAVED_SEARCH_DATASET_CRUNCHBASE &&
          search.name === selectedSavedSearch
        )
    );

    try {
      setSavedSearchesBusy(true);
      await persistSavedSearches(nextSavedSearches);
      setSavedSearches(nextSavedSearches);
      setSelectedSavedSearch("");
    } catch (err) {
      console.error("Error deleting saved search:", err);
    } finally {
      setSavedSearchesBusy(false);
    }
  };

  const saveSavedSearchForDataset = async (dataset, name, config) => {
    const trimmedName = String(name || "").trim();
    if (!trimmedName) return;

    const nextSavedSearches = upsertSavedSearch(savedSearches, dataset, trimmedName, config);

    setSavedSearchesBusy(true);
    try {
      await persistSavedSearches(nextSavedSearches);
      setSavedSearches(nextSavedSearches);
    } finally {
      setSavedSearchesBusy(false);
    }
  };

  const deleteSavedSearchForDataset = async (dataset, name) => {
    const trimmedName = String(name || "").trim();
    if (!trimmedName) return;

    const normalizedDataset = getSavedSearchDataset(dataset);
    const nextSavedSearches = savedSearches.filter(
      (search) =>
        !(
          getSavedSearchDataset(search.dataset) === normalizedDataset &&
          search.name === trimmedName
        )
    );

    setSavedSearchesBusy(true);
    try {
      await persistSavedSearches(nextSavedSearches);
      setSavedSearches(nextSavedSearches);
    } finally {
      setSavedSearchesBusy(false);
    }
  };

  const companiesWithOverrides = companiesWithCrossDatasetMatches.map((company) =>
    applyFeedbackOverrides(company, feedbackData[company.id])
  );

  const handleClassificationChange = (companyName, nextType) => {
    setOverrideNotice(
      `${companyName} moved to ${getLeadTypeLabel(nextType)}.`
    );
  };

  const assignmentCompanies =
    assignmentView === "ALL" && canManageAssignments
      ? new Set(assignments.map((assignment) => getAssignmentKey(assignment.companyName)))
      : new Set(
          assignments
            .filter((assignment) => assignment.assignedTo === currentUserEmail)
            .map((assignment) => getAssignmentKey(assignment.companyName))
        );

  const selectedGraphCompanyNameKeys = new Set(
    selectedGraphCompanyNames.map(normalizeKey).filter(Boolean)
  );

  const baseFilteredCompanies = companiesWithOverrides
    .filter((c) =>
      currentTab === "OVERVIEW"
        ? true
        : currentTab === "NEW_ADDITIONS"
        ? c["Updated List Entry"]
        : currentTab === "ARCHIVED"
        ? c.Archived
        : currentTab === "ASSIGNMENTS"
        ? assignmentCompanies.has(getAssignmentKey(c["Organization Name"]))
        : c["Lead Type"] === currentTab
    )
    .filter((c) => matchesSearch(c, searchTerm))
    .filter((c) =>
      selectedGraphCompanyNameKeys.size > 0
        ? selectedGraphCompanyNameKeys.has(normalizeKey(c["Organization Name"]))
        : true
    )
    .filter((c) =>
      selectedIndustry.length > 0
        ? selectedIndustry.some((industry) =>
            c.BucketIndustries.includes(industry)
          )
        : true
    )
    .filter((c) =>
      selectedCountry.length > 0
        ? selectedCountry.includes(c["Headquarters Location"])
        : true
    )
    .filter((c) =>
      selectedStage.length > 0
        ? selectedStage.includes(c["Investment Stage"])
        : true
    )
    .filter((c) =>
      matchedState === MATCH_STATE_GLOBALDATA_MATCHED
        ? Boolean(c["GlobalData Matching Name"])
        : matchedState === MATCH_STATE_GLOBALDATA_UNMATCHED
        ? !Boolean(c["GlobalData Matching Name"])
        : matchedState === MATCH_STATE_COMPANYHOUSE_MATCHED
        ? Boolean(c["Company House Matching Name"])
        : matchedState === MATCH_STATE_COMPANYHOUSE_UNMATCHED
        ? !Boolean(c["Company House Matching Name"])
        : matchedState === MATCH_STATE_ALL_UNMATCHED
        ? !Boolean(c["GlobalData Matching Name"]) &&
          !Boolean(c["Company House Matching Name"])
        : true
    )
    .filter((c) =>
      clientState === CLIENT_STATE_CONTACTED
        ? Boolean(c.contacted ?? c.Contacted)
        : true
    )
    .filter((c) =>
      clientState === CLIENT_STATE_CONVERTED
        ? c["Client Lifecycle Status"] === CLIENT_LIFECYCLE_CONVERTED
        : clientState === CLIENT_STATE_CLIENT
        ? c["Client Lifecycle Status"] === CLIENT_LIFECYCLE_EXISTING
        : clientState === CLIENT_STATE_NOT_CLIENT_ANYMORE
        ? c["Client Lifecycle Status"] === CLIENT_LIFECYCLE_NOT_CLIENT_ANYMORE
        : clientState === CLIENT_STATE_NON_CLIENT
        ? c["Client Lifecycle Status"] === CLIENT_LIFECYCLE_NON_CLIENT
        : true
    )
    .filter((c) =>
      selectedUpdateCategory.length > 0
        ? selectedUpdateCategory.includes("Archived")
          ? Boolean(c.Archived)
          : selectedUpdateCategory.some((category) =>
              (c["Update Categories"] || []).includes(category)
            )
        : true
    )
    .filter((c) =>
      selectedEstimatedRevenueRange.length > 0
        ? selectedEstimatedRevenueRange.includes(c["Estimated Revenue Range"])
        : true
    )
    .filter((c) =>
      selectedCompanyAge.length > 0
        ? selectedCompanyAge.includes(getCompanyAgeBucket(c["Founded"]))
        : true
    )
    .filter((c) => matchesPeopleRoleFilter(c, selectedPeopleRoles))
    .filter((c) =>
      selectedInvestorType.length > 0
        ? selectedInvestorType.includes(c["Investor Type"])
        : true
    )
    .filter((c) =>
      selectedFundingYear.length > 0
        ? selectedFundingYear.includes(c["Funding Year"])
        : true
    )
    .filter((c) =>
      currentTab === "ACCEPT" ||
      currentTab === "NOT ACCEPT" ||
      currentTab === "FUZZY"
        ? true
        : selectedLeadStatus.length > 0
        ? selectedLeadStatus.includes(c["Lead Type"])
        : true
    )
    .filter((c) =>
      selectedManualChange === "manual-only"
        ? c["Manual Override"]
        : selectedManualChange === "no-manual"
        ? !c["Manual Override"]
        : true
    );

  // -------------------------------------------------------
  // FILTERING & SORTING
  // -------------------------------------------------------
  const filtered = baseFilteredCompanies
    .filter((c) =>
      selectedFundingPeriod.length > 0
        ? selectedFundingPeriod.includes(c["Funding Quarter"]) ||
          selectedFundingPeriod.includes(c["Funding Month"])
        : true
    );

  const quarterChartCompanies =
    selectedFundingPeriod.length > 0 &&
    selectedFundingPeriod.some((period) => !QUARTER_OPTIONS.includes(period))
      ? baseFilteredCompanies
      : filtered;

  const leadStatusFilterDisabled =
    currentTab === "ACCEPT" ||
    currentTab === "NOT ACCEPT" ||
    currentTab === "FUZZY";

  const sorted = [...filtered].sort((a, b) => {
    const compareNamesAsc = () =>
      a["Organization Name"].localeCompare(b["Organization Name"]);

    if (sortBy === "name-asc")
      return compareNamesAsc();
    if (sortBy === "name-desc")
      return b["Organization Name"].localeCompare(a["Organization Name"]);

    if (sortBy === "rank-asc")
      return (a["Crunchbase Rank"] || 999999) -
             (b["Crunchbase Rank"] || 999999);

    if (sortBy === "rank-desc")
      return (b["Crunchbase Rank"] || 0) -
             (a["Crunchbase Rank"] || 0);

    const parseEmployees = (value) => {
      if (!value) return 0;

      if (typeof value === "number") return value;

      if (value.includes("-")) {
        const [min, max] = value.split("-").map((n) => parseInt(n.trim(), 10));
        return (min + max) / 2;
      }

      return parseInt(value, 10) || 0;
    };

    if (sortBy === "employees-desc")
      return parseEmployees(b.Employees) - parseEmployees(a.Employees);

    if (sortBy === "employees-asc")
      return parseEmployees(a.Employees) - parseEmployees(b.Employees);

    const archivedDiff = Number(a.Archived) - Number(b.Archived);
    if (archivedDiff !== 0) {
      return archivedDiff;
    }

    const leadTypeOrderDiff =
      (LEAD_TYPE_SORT_ORDER[a["Lead Type"]] ?? Number.MAX_SAFE_INTEGER) -
      (LEAD_TYPE_SORT_ORDER[b["Lead Type"]] ?? Number.MAX_SAFE_INTEGER);

    if (leadTypeOrderDiff !== 0) {
      return leadTypeOrderDiff;
    }

    return compareNamesAsc();
  });

  const hasActiveQuery =
    searchTerm.trim() !== "" ||
    selectedGraphCompanyNames.length > 0 ||
    selectedIndustry.length > 0 ||
    selectedCountry.length > 0 ||
    selectedStage.length > 0 ||
    matchedState !== "all" ||
    clientState !== "all" ||
    selectedUpdateCategory.length > 0 ||
    selectedEstimatedRevenueRange.length > 0 ||
    selectedCompanyAge.length > 0 ||
    selectedPeopleRoles.length > 0 ||
    selectedInvestorType.length > 0 ||
    selectedFundingPeriod.length > 0 ||
    selectedFundingYear.length > 0 ||
    selectedLeadStatus.length > 0 ||
    selectedManualChange !== "";
  const overviewCompanies = sorted;
  const overviewVisibleCompanies = overviewCompanies.slice(0, visibleCount);
  const crunchbaseActiveMatchLabels = getMatchLabels(matchedState, "globaldata");
  const matchedCompanyCount = sorted.filter((company) =>
    crunchbaseActiveMatchLabels.target === "companyhouse"
      ? Boolean(company["Company House Matching Name"])
      : Boolean(company["GlobalData Matching Name"])
  ).length;
  const unmatchedCompanyCount = sorted.length - matchedCompanyCount;
  const companyListSummaryLabel =
    currentTab === "ACCEPT"
      ? "Total Leads"
      : currentTab === "NOT ACCEPT"
      ? "Total Discard"
      : currentTab === "FUZZY"
      ? "Total Unsure"
      : currentTab === "ARCHIVED"
      ? "Total Archived"
      : currentTab === "ASSIGNMENTS"
      ? assignmentView === "ALL" && canManageAssignments
        ? "Total Assigned Companies"
        : "Total My Leads"
      : "Total Crunchbase companies";
  const noCrunchbaseCompaniesMessage =
    currentTab === "ASSIGNMENTS"
      ? assignmentView === "ALL" && canManageAssignments
        ? "No assigned companies match the current filters."
        : "No companies are assigned to your user."
      : "No companies matching this search criteria";
  const crunchbaseCriteriaExtractionSections = (() => {
    const companyCountColumns = createCountColumns("Category", "Companies");
    const makeSection = ({
      id,
      title,
      subtitle,
      label,
      rows,
      emptyLabel,
      maxHeight = 260,
    }) => ({
      id,
      title,
      subtitle,
      columns: createCountColumns(label, "Companies"),
      rows,
      emptyLabel,
      minHeight: 0,
      maxHeight,
    });

    return [
      makeSection({
        id: "industry-distribution",
        title: "Industry Distribution",
        subtitle: "Number of companies per industry bucket",
        label: "Industry",
        rows: buildCrunchbaseCriteriaRows(sorted, (company) => company.BucketIndustries || []),
        emptyLabel: "No industry distribution available for the selected criteria.",
      }),
      makeSection({
        id: "geographic-distribution",
        title: "Geographic Distribution",
        subtitle: "Number of companies per headquarters location",
        label: "Headquarters Location",
        rows: buildCrunchbaseCriteriaRows(
          sorted,
          (company) => company["Headquarters Location"] || "Unknown"
        ),
        emptyLabel: "No geographic distribution available for the selected criteria.",
        maxHeight: 320,
      }),
      makeSection({
        id: "funding-period-distribution",
        title: "Funding Distribution Over Time",
        subtitle: "Number of companies per funding quarter",
        label: "Funding Quarter",
        rows: buildCrunchbaseCriteriaRows(sorted, (company) => company["Funding Quarter"]),
        emptyLabel: "No funding period distribution available for the selected criteria.",
      }),
      makeSection({
        id: "funding-type-distribution",
        title: "Funding Type Distribution",
        subtitle: "Number of companies per funding type",
        label: "Funding Type",
        rows: buildCrunchbaseCriteriaRows(
          sorted,
          (company) => company["Investment Stage"] || "Unknown"
        ),
        emptyLabel: "No funding type distribution available for the selected criteria.",
      }),
      makeSection({
        id: "company-age-distribution",
        title: "Company Age Distribution",
        subtitle: "Number of companies per company age range",
        label: "Company Age",
        rows: buildCrunchbaseCriteriaRows(sorted, (company) =>
          getCompanyAgeBucket(company["Founded"])
        ),
        emptyLabel: "No company age distribution available for the selected criteria.",
      }),
    ].map((section) => ({
      ...section,
      columns: section.columns || companyCountColumns,
    }));
  })();
  const hasCustomUpdateWindow =
    Boolean(updatedWindowFrom || updatedWindowTo) &&
    (updatedWindowFrom !== (updateWindowConfig?.selectedFrom || "") ||
      updatedWindowTo !== (updateWindowConfig?.selectedTo || ""));
  const crunchbaseVisualisationFilterSnapshot = (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" sx={{ mt: 0, mb: 1 }}>
        Active Filter Snapshot
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {searchTerm.trim() !== "" && (
          <Chip label={`Search: ${searchTerm.trim()}`} onDelete={() => setSearchTerm("")} />
        )}
        {selectedGraphCompanyNames.length > 0 && (
          <Chip
            label={
              selectedGraphCompanyNames.length === 1
                ? `Company: ${selectedGraphCompanyNames[0]}`
                : `Companies: ${selectedGraphCompanyNames.length} selected`
            }
            onDelete={() => {
              setSelectedGraphCompanyNames([]);
              setVisibleCount(20);
            }}
          />
        )}
        {hasCustomUpdateWindow && updatedWindowFrom && (
          <Chip
            label={`From: ${formatIsoDateForDisplay(updatedWindowFrom)}`}
            onDelete={() => setUpdatedWindowFrom(updateWindowConfig?.selectedFrom || "")}
          />
        )}
        {hasCustomUpdateWindow && updatedWindowTo && (
          <Chip
            label={`To: ${formatIsoDateForDisplay(updatedWindowTo)}`}
            onDelete={() => setUpdatedWindowTo(updateWindowConfig?.selectedTo || "")}
          />
        )}
        {selectedIndustry.map((value) => (
          <Chip
            key={`industry-${value}`}
            label={`Industry: ${value}`}
            onDelete={() =>
              setSelectedIndustry((current) => current.filter((entry) => entry !== value))
            }
          />
        ))}
        {selectedCountry.map((value) => (
          <Chip
            key={`country-${value}`}
            label={`Country: ${value}`}
            onDelete={() =>
              setSelectedCountry((current) => current.filter((entry) => entry !== value))
            }
          />
        ))}
        {selectedStage.map((value) => (
          <Chip
            key={`stage-${value}`}
            label={`Stage: ${value}`}
            onDelete={() =>
              setSelectedStage((current) => current.filter((entry) => entry !== value))
            }
          />
        ))}
        {matchedState !== "all" && (
          <Chip
            label={
              matchedState === MATCH_STATE_GLOBALDATA_MATCHED
                ? "Matched to GlobalData only"
                : matchedState === MATCH_STATE_GLOBALDATA_UNMATCHED
                ? "Unmatched to GlobalData only"
                : matchedState === MATCH_STATE_COMPANYHOUSE_MATCHED
                ? "Matched to Company House only"
                : matchedState === MATCH_STATE_COMPANYHOUSE_UNMATCHED
                ? "Unmatched to Company House only"
                : "Unmatched across all datasets"
            }
            onDelete={() => setMatchedState("all")}
          />
        )}
        {clientState !== "all" && (
          <Chip
            label={getClientStateFilterLabel(clientState)}
            onDelete={() => setClientState("all")}
          />
        )}
        {selectedUpdateCategory.map((value) => (
          <Chip
            key={`update-category-${value}`}
            label={`Update: ${value}`}
            onDelete={() =>
              setSelectedUpdateCategory((current) => current.filter((entry) => entry !== value))
            }
          />
        ))}
        {selectedEstimatedRevenueRange.map((value) => (
          <Chip
            key={`revenue-${value}`}
            label={`Revenue: ${value}`}
            onDelete={() =>
              setSelectedEstimatedRevenueRange((current) =>
                current.filter((entry) => entry !== value)
              )
            }
          />
        ))}
        {selectedCompanyAge.map((value) => (
          <Chip
            key={`company-age-${value}`}
            label={`Age: ${value}`}
            onDelete={() =>
              setSelectedCompanyAge((current) => current.filter((entry) => entry !== value))
            }
          />
        ))}
        {selectedPeopleRoles.map((value) => (
          <Chip
            key={`people-role-${value}`}
            label={`People: ${value}`}
            onDelete={() =>
              setSelectedPeopleRoles((current) =>
                current.filter((entry) => entry !== value)
              )
            }
          />
        ))}
        {selectedInvestorType.map((value) => (
          <Chip
            key={`investor-type-${value}`}
            label={`Investor: ${value}`}
            onDelete={() =>
              setSelectedInvestorType((current) => current.filter((entry) => entry !== value))
            }
          />
        ))}
        {selectedFundingPeriod.map((value) => (
          <Chip
            key={`funding-period-${value}`}
            label={`Funding Period: ${value}`}
            onDelete={() =>
              setSelectedFundingPeriod((current) => current.filter((entry) => entry !== value))
            }
          />
        ))}
        {selectedFundingYear.map((value) => (
          <Chip
            key={`funding-year-${value}`}
            label={`Funding Year: ${value}`}
            onDelete={() =>
              setSelectedFundingYear((current) => current.filter((entry) => entry !== value))
            }
          />
        ))}
        {selectedLeadStatus.map((value) => (
          <Chip
            key={`lead-status-${value}`}
            label={`Lead Status: ${getLeadTypeLabel(value)}`}
            onDelete={() =>
              setSelectedLeadStatus((current) => current.filter((entry) => entry !== value))
            }
          />
        ))}
        {selectedManualChange === "manual-only" && (
          <Chip label="Manual Changes Only" onDelete={() => setSelectedManualChange("")} />
        )}
        {selectedManualChange === "no-manual" && (
          <Chip label="No Manual Changes" onDelete={() => setSelectedManualChange("")} />
        )}
        {!hasCustomUpdateWindow &&
          searchTerm.trim() === "" &&
          selectedGraphCompanyNames.length === 0 &&
          selectedIndustry.length === 0 &&
          selectedCountry.length === 0 &&
          selectedStage.length === 0 &&
          matchedState === "all" &&
          clientState === "all" &&
          selectedUpdateCategory.length === 0 &&
          selectedEstimatedRevenueRange.length === 0 &&
          selectedCompanyAge.length === 0 &&
          selectedPeopleRoles.length === 0 &&
          selectedInvestorType.length === 0 &&
          selectedFundingPeriod.length === 0 &&
          selectedFundingYear.length === 0 &&
          selectedLeadStatus.length === 0 &&
          selectedManualChange === "" && (
            <Typography sx={{ color: "#64748b", fontSize: "0.9rem" }}>
              No filters applied. Charts reflect the current Crunchbase search universe.
            </Typography>
          )}
      </Box>
    </Box>
  );

  const crunchbaseExportFilters = buildExportFilterRows("Crunchbase", [
        { label: "Search", value: searchTerm.trim() },
        {
          label: "Selected Company",
          value:
            selectedGraphCompanyNames.length === 1
              ? selectedGraphCompanyNames[0]
              : "",
        },
        {
          label: "Selected Companies",
          value:
            selectedGraphCompanyNames.length > 1
              ? `${selectedGraphCompanyNames.length} selected`
              : "",
        },
        {
          label: "Updated From",
          value:
            hasCustomUpdateWindow && updatedWindowFrom
              ? formatIsoDateForDisplay(updatedWindowFrom)
              : "",
        },
        {
          label: "Updated To",
          value:
            hasCustomUpdateWindow && updatedWindowTo
              ? formatIsoDateForDisplay(updatedWindowTo)
              : "",
        },
        { label: "Industry", value: selectedIndustry },
        { label: "Country", value: selectedCountry },
        { label: "Stage", value: selectedStage },
        {
          label: "Match Status",
          value:
            matchedState === MATCH_STATE_GLOBALDATA_MATCHED
              ? "Matched to GlobalData only"
              : matchedState === MATCH_STATE_GLOBALDATA_UNMATCHED
              ? "Unmatched to GlobalData only"
              : matchedState === MATCH_STATE_COMPANYHOUSE_MATCHED
              ? "Matched to Company House only"
              : matchedState === MATCH_STATE_COMPANYHOUSE_UNMATCHED
              ? "Unmatched to Company House only"
              : matchedState === "all_unmatched"
              ? "Unmatched across all datasets"
              : "",
        },
        {
          label: "List Status",
          value: clientState !== "all" ? getClientStateFilterLabel(clientState) : "",
        },
        { label: "Update Category", value: selectedUpdateCategory },
        { label: "Revenue", value: selectedEstimatedRevenueRange },
        { label: "Age", value: selectedCompanyAge },
        { label: "People", value: selectedPeopleRoles },
        { label: "Investor", value: selectedInvestorType },
        { label: "Funding Period", value: selectedFundingPeriod },
        { label: "Funding Year", value: selectedFundingYear },
        { label: "Lead Status", value: selectedLeadStatus.map((value) => getLeadTypeLabel(value)) },
        {
          label: "Manual Changes",
          value:
            selectedManualChange === "manual-only"
              ? "Manual Changes Only"
              : selectedManualChange === "no-manual"
              ? "No Manual Changes"
              : "",
        },
      ]);

  // -------------------------------------------------------
  // LOADING GATE
  // -------------------------------------------------------
  if (!loadedFeedback) return <div>Loading...</div>;

  // -------------------------------------------------------
  // RENDER UI
  // -------------------------------------------------------
  return (
    <>
      <Header
        currentUser={authSession?.user}
        userConfiguration={userConfiguration}
        onChangePassword={handleChangePassword}
        onLogout={handleLogout}
        onSaveUserConfiguration={handleSaveUserConfiguration}
        showCommentNotifications={canManageAssignments}
        commentNotifications={commentNotifications}
        commentNotificationsLoading={commentNotificationsLoading}
        onMarkAllCommentNotificationsRead={markCommentNotificationsSeen}
        onCommentNotificationSelect={handleCommentNotificationSelect}
        canImportSalesforce={canImportSalesforce}
        onSalesforceImport={importSalesforceWorkbook}
        onHomeClick={handleHomeClick}
      />

      <Container
        maxWidth="xl"
        sx={{
          paddingTop: { xs: 11, md: 19 },
          paddingBottom: 2,
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ display: "none" }}>
          Brokancode – Pharmaron: Lead Discovery PoC
        </Typography>

        {workspaceDataset === "globaldata" ? (
          <GlobaldataWorkspace
            apiEnabled={DATA_SOURCE === "api"}
            postJson={postJson}
            deleteJson={deleteJson}
            postApiFile={postApiFile}
            getApiJson={getApiJson}
            onOpenCrunchbaseMatch={openCrunchbaseCompanyMatch}
            onOpenCompanyHouseMatch={openCompanyHouseCompanyMatch}
            initialQueryConfig={initialQueryConfig}
            workspaceDataset={workspaceDataset}
            onWorkspaceDatasetChange={switchWorkspaceDataset}
            savedSearches={globaldataSavedSearches}
            savedSearchesBusy={savedSearchesBusy}
            onSaveSavedSearch={(name, config) =>
              saveSavedSearchForDataset(SAVED_SEARCH_DATASET_GLOBALDATA, name, config)
            }
            onDeleteSavedSearch={(name) =>
              deleteSavedSearchForDataset(SAVED_SEARCH_DATASET_GLOBALDATA, name)
            }
            getAssignmentForCompanyName={getAssignmentForCompanyName}
            assignedCompanyNames={globaldataAssignedCompanyNames}
            assignmentView={assignmentView}
            setAssignmentView={setAssignmentView}
            assignmentsLoading={assignmentsLoading}
            canManageAssignments={canManageAssignments}
            assignableUsers={assignableUsers}
            assignableUsersLoading={assignableUsersLoading}
            onAssignmentsChanged={loadAssignments}
            dataRefreshToken={salesforceImportRefreshToken}
          />
        ) : workspaceDataset === WORKSPACE_DATASET_COMPANYHOUSE ? (
          <CompanyHouseWorkspace
            apiEnabled={DATA_SOURCE === "api"}
            postJson={postJson}
            deleteJson={deleteJson}
            postApiFile={postApiFile}
            getApiJson={getApiJson}
            initialQueryConfig={initialQueryConfig}
            workspaceDataset={workspaceDataset}
            onWorkspaceDatasetChange={switchWorkspaceDataset}
            onOpenCrunchbaseMatch={openCrunchbaseCompanyMatch}
            onOpenGlobaldataMatch={openGlobaldataCompanyMatch}
            getAssignmentForCompanyName={getAssignmentForCompanyName}
            assignedCompanyNames={companyHouseAssignedCompanyNames}
            assignmentView={assignmentView}
            setAssignmentView={setAssignmentView}
            assignmentsLoading={assignmentsLoading}
            canManageAssignments={canManageAssignments}
            assignableUsers={assignableUsers}
            assignableUsersLoading={assignableUsersLoading}
            onAssignmentsChanged={loadAssignments}
            dataRefreshToken={salesforceImportRefreshToken}
          />
        ) : (
          <>
            <Box
              sx={{
                position: { xs: "static", md: "fixed" },
                top: { md: 76 },
                left: { md: 0 },
                right: { md: 0 },
                zIndex: 11,
                mb: 0.25,
              }}
            >
              <Paper
                sx={{
                  py: 1,
                  borderRadius: 0,
                  backgroundColor: "#ffffff",
                  border: "none",
                  boxShadow: "none",
                }}
              >
                <Container maxWidth="xl">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: { xs: 1, md: 1.5 },
                    flexWrap: "nowrap",
                    overflowX: "auto",
                    width: "100%",
                    px: { xs: 0, md: 1.5 },
                  }}
                >
                    <Typography
                      sx={{
                        display: "block",
                        color: "#94a3b8",
                        fontSize: { xs: "0.72rem", md: "0.8rem" },
                        fontWeight: 700,
                        letterSpacing: { xs: "0.05em", md: "0.08em" },
                        textTransform: "uppercase",
                        pl: { xs: 0, md: 1.5 },
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      Workspace View
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "nowrap",
                        marginLeft: "auto",
                        gap: 1,
                        width: "fit-content",
                        maxWidth: "100%",
                        minWidth: 0,
                        flexShrink: 0,
                      }}
                    >
                      <Button
                        variant="outlined"
                        endIcon={<KeyboardArrowDown fontSize="small" />}
                        onClick={openWorkspaceDatasetMenu}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          borderRadius: 999,
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                          px: useCompactRightPanelTabs ? 0.8 : 1.5,
                          minWidth: useCompactRightPanelTabs ? 82 : 128,
                          fontSize: useCompactRightPanelTabs ? "0.74rem" : "0.88rem",
                        }}
                      >
                        {useCompactRightPanelTabs
                          ? `DS: ${workspaceDataset === "globaldata" ? "GD" : workspaceDataset === WORKSPACE_DATASET_COMPANYHOUSE ? "CH" : "CB"}`
                          : `Data Source: ${workspaceDataset === "globaldata" ? "GD" : workspaceDataset === WORKSPACE_DATASET_COMPANYHOUSE ? "CH" : "CB"}`}
                      </Button>
                      <Tabs
                        data-right-panel-tabs="true"
                        value={rightPanelView}
                        onChange={(e, value) => setRightPanelView(value)}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{
                          flex: "0 0 auto",
                          display: "inline-flex",
                          minWidth: 0,
                          flexShrink: 0,
                          minHeight: "auto",
                          width: "fit-content",
                          maxWidth: "100%",
                          "& .MuiTabs-scroller": {
                            overflow: "visible !important",
                          },
                          "& .MuiTabs-indicator": { display: "none" },
                          "& .MuiTabs-flexContainer": {
                            gap: 0.5,
                            flexWrap: "nowrap",
                          },
                          "& .MuiTab-root": {
                            minHeight: { xs: 34, md: 38 },
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: useCompactRightPanelTabs ? "0.74rem" : "0.88rem",
                            borderRadius: 999,
                            px: useCompactRightPanelTabs ? 0.9 : 1.6,
                            py: 0.5,
                            color: "#475569",
                            minWidth: useCompactRightPanelTabs ? 92 : 136,
                          },
                          "& .Mui-selected": {
                            color: "#0f172a !important",
                            backgroundColor: "#eef2f7",
                          },
                        }}
                      >
                        <Tab
                          label={useCompactRightPanelTabs ? "Charts" : "Visualisations"}
                          value="visualisations"
                        />
                        <Tab
                          label="Network"
                          value="network"
                        />
                        <Tab
                          label={useCompactRightPanelTabs ? "List" : "Company List"}
                          value="companies"
                        />
                      </Tabs>
                      <Menu
                        anchorEl={workspaceDatasetMenuAnchor}
                        open={Boolean(workspaceDatasetMenuAnchor)}
                        onClose={closeWorkspaceDatasetMenu}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        PaperProps={{
                          sx: {
                            mt: 1,
                            minWidth: 220,
                            borderRadius: 2,
                            boxShadow: "0 16px 40px rgba(15, 23, 42, 0.16)",
                          },
                        }}
                      >
                        <MenuItem
                          selected={workspaceDataset === "crunchbase"}
                          onClick={() => switchWorkspaceDataset("crunchbase")}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {workspaceDataset === "crunchbase" ? <Check fontSize="small" /> : null}
                          </ListItemIcon>
                          <ListItemText primary="Crunchbase" />
                        </MenuItem>
                        <MenuItem
                          selected={workspaceDataset === "globaldata"}
                          onClick={() => switchWorkspaceDataset("globaldata")}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {workspaceDataset === "globaldata" ? <Check fontSize="small" /> : null}
                          </ListItemIcon>
                          <ListItemText primary="GlobalData" />
                        </MenuItem>
                        <MenuItem
                          selected={workspaceDataset === WORKSPACE_DATASET_COMPANYHOUSE}
                          onClick={() => switchWorkspaceDataset(WORKSPACE_DATASET_COMPANYHOUSE)}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {workspaceDataset === WORKSPACE_DATASET_COMPANYHOUSE ? <Check fontSize="small" /> : null}
                          </ListItemIcon>
                          <ListItemText primary="Company House" />
                        </MenuItem>
                      </Menu>
                    </Box>
                  </Box>
                </Container>
              </Paper>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "340px minmax(0, 1fr)" },
                gap: { xs: 1.5, md: 2, lg: 2.5 },
                alignItems: "start",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: { xs: "none", lg: 340 },
                  justifySelf: { xs: "stretch", lg: "center" },
                  alignSelf: { lg: "start" },
                }}
              >
                <Box
                    sx={{
                      display: "grid",
                      gap: 1,
                      position: { xs: "static", lg: "fixed" },
                      top: { lg: 152 },
                      left: { lg: "max(24px, calc((100vw - 1536px) / 2 + 24px))" },
                    width: { xs: "100%", lg: 340 },
                    maxWidth: { lg: 340 },
                    boxSizing: "border-box",
                    alignContent: { lg: "start" },
                    maxHeight: { lg: "calc(100vh - 188px)" },
                    overflowY: { lg: "auto" },
                    pr: { lg: 0.5 },
                    scrollbarGutter: { lg: "stable" },
                  }}
                >
            <Paper
              sx={{
                p: 1.25,
                borderRadius: 3,
                backgroundColor: "#ffffff",
                border: "1px solid rgba(15, 23, 42, 0.06)",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
              }}
            >
              <Typography variant="h6" sx={{ mb: 0.85, fontWeight: 700, fontSize: "0.95rem" }}>
                Match Status
              </Typography>
              <SingleSelectionAutocomplete
                label="Match Status"
                value={matchedState}
                onChange={(value) => setMatchedState(value || MATCH_STATE_ALL)}
                options={CRUNCHBASE_MATCH_STATUS_OPTIONS}
              />
            </Paper>

            <Paper
              sx={{
                p: 1.25,
                borderRadius: 3,
                backgroundColor: "#ffffff",
                border: "1px solid rgba(15, 23, 42, 0.06)",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
              }}
            >
              <Typography variant="h6" sx={{ mb: 0.85, fontWeight: 700, fontSize: "0.95rem" }}>
                List Status
              </Typography>
              <SingleSelectionAutocomplete
                label="List Status"
                value={clientState}
                onChange={(value) => setClientState(value || "all")}
                options={CRUNCHBASE_LIST_STATUS_OPTIONS}
              />
            </Paper>

            <Paper
              sx={{
                p: 0.5,
                borderRadius: 3,
                backgroundColor: "#ffffff",
                border: "1px solid rgba(15, 23, 42, 0.06)",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
              }}
            >
              <Tabs
                value={currentTab}
                onChange={(e, v) => {
                  setCurrentTab(v);
                  setVisibleCount(20);
                }}
                orientation="vertical"
                variant="scrollable"
                textColor="primary"
                indicatorColor="primary"
                sx={{
                  minHeight: "auto",
                  "& .MuiTabs-flexContainer": {
                    alignItems: "stretch",
                  },
                  "& .MuiTab-root": {
                    alignItems: "flex-start",
                    textAlign: "left",
                    minHeight: 36,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    lineHeight: 1.2,
                    borderRadius: 2,
                    mx: 0.5,
                    px: 1,
                    py: 0.5,
                  },
                }}
              >
                <Tab label="Updated List" value="NEW_ADDITIONS" />
                <Tab label="Overview" value="OVERVIEW" />
                <Tab label="Leads" value="ACCEPT" />
                <Tab label="Discard" value="NOT ACCEPT" />
                <Tab label="Unsure" value="FUZZY" />
                <Tab label="Archived" value="ARCHIVED" />
                <Tab label="Assignments" value="ASSIGNMENTS" />
              </Tabs>
            </Paper>

            {currentTab === "ASSIGNMENTS" && (
              <Paper
                sx={{
                  p: 1.25,
                  borderRadius: 3,
                  backgroundColor: "#ffffff",
                  border: "1px solid rgba(15, 23, 42, 0.06)",
                  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
                }}
              >
                <Typography variant="h6" sx={{ mb: 0.85, fontWeight: 700, fontSize: "0.95rem" }}>
                  Assignment View
                </Typography>
                <Box sx={{ display: "grid", gap: 1 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Scope</InputLabel>
                    <Select
                      value={assignmentView}
                      label="Scope"
                      onChange={(event) => setAssignmentView(event.target.value)}
                    >
                      <MenuItem value="MY_LEADS">My Leads</MenuItem>
                      <MenuItem value="ALL" disabled={!canManageAssignments}>
                        All Assignments
                      </MenuItem>
                    </Select>
                  </FormControl>
                  {assignmentsLoading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#64748b" }}>
                      <CircularProgress size={16} />
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        Loading assignments...
                      </Typography>
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: "0.85rem", color: "#64748b" }}>
                      {assignmentView === "ALL" && canManageAssignments
                        ? "Shows every currently assigned company."
                        : "Shows companies assigned to your user."}
                    </Typography>
                  )}
                </Box>
              </Paper>
            )}

            <Paper
              sx={{
                p: 1.25,
                borderRadius: 3,
                backgroundColor: "#ffffff",
                border: "1px solid rgba(15, 23, 42, 0.06)",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
              }}
            >
              <Accordion
                disableGutters
                elevation={0}
                sx={{
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  borderRadius: "14px !important",
                  backgroundColor: "#f8fafc",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreRoundedIcon />}
                  sx={{
                    px: 1.25,
                    minHeight: 46,
                    "& .MuiAccordionSummary-content": {
                      my: 0.75,
                    },
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#1f2937" }}>
                      Export
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: "#64748b" }}>
                      {selectedCrunchbaseExportFields.length} fields selected
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    display: "grid",
                    gap: 1,
                    px: 1.25,
                    pt: 0,
                    pb: 1.25,
                  }}
                >
                  <TextField
                    size="small"
                    label="File Name"
                    value={exportFileName}
                    onChange={(e) => setExportFileName(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FileDownloadOutlinedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <FormControl size="small">
                    <InputLabel id="crunchbase-export-format-label">Format</InputLabel>
                    <Select
                      labelId="crunchbase-export-format-label"
                      label="Format"
                      value={exportFormat}
                      onChange={(event) => setExportFormat(event.target.value)}
                    >
                      {EXPORT_FORMAT_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <MultiSelectionAutocomplete
                    label="Fields"
                    values={selectedCrunchbaseExportFields}
                    onChange={setSelectedCrunchbaseExportFields}
                    options={CRUNCHBASE_EXPORT_FIELD_OPTIONS}
                    showCheckboxes
                  />
                  <Button
                    variant="contained"
                    color="success"
                    disabled={selectedCrunchbaseExportFields.length === 0}
                    onClick={() =>
                      exportFeedbackToWorkbook({
                        companies: sorted,
                        feedbackData,
                        selectedFields: selectedCrunchbaseExportFields,
                        fileName: exportFileName,
                        format: exportFormat,
                        getApiJson,
                        fieldOptions: CRUNCHBASE_EXPORT_FIELD_OPTIONS,
                        exportFilters: crunchbaseExportFilters,
                      })
                    }
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 999,
                      boxShadow: "none",
                    }}
                  >
                    {`Download ${getExportDownloadLabel(exportFormat)}`}
                  </Button>
                </AccordionDetails>
              </Accordion>
            </Paper>

            <Paper
              sx={{
                p: 1.25,
                borderRadius: 3,
                backgroundColor: "#ffffff",
                border: "1px solid rgba(15, 23, 42, 0.06)",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
              }}
            >
              <Typography variant="h6" sx={{ mb: 0.85, fontWeight: 700, fontSize: "0.95rem" }}>
                Search and Saved Views
              </Typography>
              <SearchBar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                selectedCompanyNames={selectedGraphCompanyNames}
                onAddSelectedCompanyName={addManualGraphCompanyFilter}
                onRemoveSelectedCompanyName={removeSelectedGraphCompanyName}
                onClearSelectedCompanyNames={() => {
                  setSelectedGraphCompanyNames([]);
                  setVisibleCount(20);
                }}
                savedSearchName={savedSearchName}
                setSavedSearchName={setSavedSearchName}
                saveCurrentSearch={saveCurrentSearch}
                savedSearches={crunchbaseSavedSearches}
                selectedSavedSearch={selectedSavedSearch}
                loadSavedSearch={loadSavedSearch}
                deleteSavedSearch={deleteSavedSearch}
                savedSearchesBusy={savedSearchesBusy}
              />
            </Paper>

            <Paper
              sx={{
                p: 1.25,
                borderRadius: 3,
                backgroundColor: "#ffffff",
                border: "1px solid rgba(15, 23, 42, 0.06)",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
              }}
            >
              <Typography variant="h6" sx={{ mb: 0.85, fontWeight: 700, fontSize: "0.95rem" }}>
                Filter Options
              </Typography>
              <Filters
                companies={companiesWithOverrides}
                selectedIndustry={selectedIndustry}
                setSelectedIndustry={setSelectedIndustry}
                selectedCountry={selectedCountry}
                setSelectedCountry={setSelectedCountry}
                selectedStage={selectedStage}
                setSelectedStage={setSelectedStage}
                selectedEstimatedRevenueRange={selectedEstimatedRevenueRange}
                setSelectedEstimatedRevenueRange={setSelectedEstimatedRevenueRange}
                selectedCompanyAge={selectedCompanyAge}
                setSelectedCompanyAge={setSelectedCompanyAge}
                selectedPeopleRoles={selectedPeopleRoles}
                setSelectedPeopleRoles={setSelectedPeopleRoles}
                selectedInvestorType={selectedInvestorType}
                setSelectedInvestorType={setSelectedInvestorType}
                selectedFundingPeriod={selectedFundingPeriod}
                setSelectedFundingPeriod={setSelectedFundingPeriod}
                selectedFundingYear={selectedFundingYear}
                setSelectedFundingYear={setSelectedFundingYear}
              />
            </Paper>

            <Paper
              sx={{
                p: 1.25,
                borderRadius: 3,
                backgroundColor: "#ffffff",
                border: "1px solid rgba(15, 23, 42, 0.06)",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
              }}
            >
              <Typography variant="h6" sx={{ mb: 0.85, fontWeight: 700, fontSize: "0.95rem" }}>
                Sort & Status
              </Typography>
              <SortBar
                companies={companiesWithOverrides}
                sortBy={sortBy}
                setSortBy={setSortBy}
                selectedLeadStatus={selectedLeadStatus}
                setSelectedLeadStatus={setSelectedLeadStatus}
                selectedUpdateCategory={selectedUpdateCategory}
                setSelectedUpdateCategory={setSelectedUpdateCategory}
                selectedManualChange={selectedManualChange}
                setSelectedManualChange={setSelectedManualChange}
                leadStatusFilterDisabled={leadStatusFilterDisabled}
              />
            </Paper>

            <Paper
              sx={{
                p: 1.25,
                borderRadius: 3,
                backgroundColor: "#ffffff",
                border: "1px solid rgba(15, 23, 42, 0.06)",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 0.85,
                }}
              >
                <ResetFilters resetAll={resetAll} sx={actionButtonStyle} />

              </Box>
            </Paper>

            </Box>
          </Box>

          <Box>
            {currentTab === "OVERVIEW" ? (
              <Box>
                {companiesLoadError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {companiesLoadError}
                  </Alert>
                )}
                {rightPanelView === "visualisations" ? (
                  <>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr 1fr", xl: "repeat(3, minmax(0, 1fr))" },
                        gap: 1.25,
                        mb: 1.5,
                      }}
                    >
                      <SummaryCard label="Companies" value={overviewCompanies.length} />
                      <SummaryCard
                        label={crunchbaseActiveMatchLabels.matched}
                        value={matchedCompanyCount}
                        tone="matched"
                        onClick={() =>
                          setMatchedState(
                            crunchbaseActiveMatchLabels.target === "companyhouse"
                              ? MATCH_STATE_COMPANYHOUSE_MATCHED
                              : MATCH_STATE_GLOBALDATA_MATCHED
                          )
                        }
                      />
                      <SummaryCard
                        label={crunchbaseActiveMatchLabels.unmatched}
                        value={unmatchedCompanyCount}
                        tone="unmatched"
                        onClick={() =>
                          setMatchedState(
                            crunchbaseActiveMatchLabels.target === "companyhouse"
                              ? MATCH_STATE_COMPANYHOUSE_UNMATCHED
                              : MATCH_STATE_GLOBALDATA_UNMATCHED
                          )
                        }
                      />
                    </Box>
                    <CriteriaExtractionPanel
                      title="Crunchbase Criteria Extraction"
                      subtitle="Tabular company research summaries for the active Crunchbase criteria."
                      summaryText={`${sorted.length.toLocaleString("en-GB")} companies`}
                      sections={crunchbaseCriteriaExtractionSections}
                      defaultFileName="crunchbase_criteria_extraction"
                    />
                    <Overview
                      companies={overviewCompanies}
                      fundingQuarterCompanies={
                        hasActiveQuery ? quarterChartCompanies : companiesWithOverrides
                      }
                      selectedFundingPeriod={selectedFundingPeriod}
                      onClassificationSelect={handleClassificationSummarySelect}
                      onFilterSelect={handleVisualisationFilterSelect}
                    />
                    {crunchbaseVisualisationFilterSnapshot}
                  </>
                ) : rightPanelView === "network" ? (
                  <NetworkGraph
                    apiEnabled={DATA_SOURCE === "api"}
                    companies={companiesWithOverrides}
                    filters={networkFilters}
                    getApiJson={getApiJson}
                    setSidebarCompanyNames={applyGraphCompanyFilter}
                  />
                ) : (
                  <>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                      Total Crunchbase companies: {overviewCompanies.length}
                    </Typography>
                    {overviewCompanies.length === 0 ? (
                      <Typography sx={{ mt: 3, fontWeight: 700, color: "#1f2937" }}>
                        {noCrunchbaseCompaniesMessage}
                      </Typography>
                    ) : (
                      overviewVisibleCompanies.map((c) => (
                        <CompanyCard
                          key={c.id}
                          company={c}
                          feedbackData={feedbackData}
                          setFeedbackData={setFeedbackData}
                          getApiJson={getApiJson}
                          onClassificationChange={handleClassificationChange}
                          postJson={postJson}
                          deleteJson={deleteJson}
                          onManualOverrideSaved={loadCompanies}
                          assignment={getAssignmentForCompanyName(c["Organization Name"])}
                          canManageAssignments={canManageAssignments}
                          assignableUsers={assignableUsers}
                          assignableUsersLoading={assignableUsersLoading}
                          onAssignmentsChanged={loadAssignments}
                          onContactedChanged={handleCrunchbaseContactedChange}
                          updatedWindowFrom={updatedWindowFrom}
                          updatedWindowTo={updatedWindowTo}
                          onOpenGlobaldataMatch={openGlobaldataCompanyMatch}
                          onOpenCompanyHouseMatch={openCompanyHouseCompanyMatch}
                        />
                      ))
                    )}

                    {visibleCount < overviewCompanies.length && (
                      <Box sx={{ textAlign: "center", mt: 2 }}>
                        <Button
                          variant="contained"
                          onClick={() => setVisibleCount(visibleCount + 20)}
                        >
                          Load More
                        </Button>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            ) : (
              <Box>
                {currentTab === "NEW_ADDITIONS" && updateWindowConfig && (
                  <UpdatedTimeline
                    minDate={effectiveTimelineMinDate}
                    maxDate={effectiveTimelineMaxDate}
                    selectedFrom={updatedWindowFrom}
                    selectedTo={updatedWindowTo}
                    onCommit={(nextFrom, nextTo) => {
                      setUpdatedWindowFrom(nextFrom);
                      setUpdatedWindowTo(nextTo);
                      setVisibleCount(20);
                    }}
                  />
                )}
                {companiesLoadError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {companiesLoadError}
                  </Alert>
                )}
                {rightPanelView === "visualisations" ? (
                  sorted.length === 0 ? (
                    <Typography sx={{ mt: 3, fontWeight: 700, color: "#1f2937" }}>
                      {noCrunchbaseCompaniesMessage}
                    </Typography>
                  ) : (
                    <>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr 1fr", xl: "repeat(3, minmax(0, 1fr))" },
                          gap: 1.25,
                          mb: 1.5,
                        }}
                      >
                        <SummaryCard label="Companies" value={sorted.length} />
                        <SummaryCard
                          label={crunchbaseActiveMatchLabels.matched}
                          value={matchedCompanyCount}
                          tone="matched"
                          onClick={() =>
                            setMatchedState(
                              crunchbaseActiveMatchLabels.target === "companyhouse"
                                ? MATCH_STATE_COMPANYHOUSE_MATCHED
                                : MATCH_STATE_GLOBALDATA_MATCHED
                            )
                          }
                        />
                        <SummaryCard
                          label={crunchbaseActiveMatchLabels.unmatched}
                          value={unmatchedCompanyCount}
                          tone="unmatched"
                          onClick={() =>
                            setMatchedState(
                              crunchbaseActiveMatchLabels.target === "companyhouse"
                                ? MATCH_STATE_COMPANYHOUSE_UNMATCHED
                                : MATCH_STATE_GLOBALDATA_UNMATCHED
                            )
                          }
                        />
                      </Box>
                      <CriteriaExtractionPanel
                        title="Crunchbase Criteria Extraction"
                        subtitle="Tabular company research summaries for the active Crunchbase criteria."
                        summaryText={`${sorted.length.toLocaleString("en-GB")} companies`}
                        sections={crunchbaseCriteriaExtractionSections}
                        defaultFileName="crunchbase_criteria_extraction"
                      />
                      <TabMiniCharts
                        companies={sorted}
                        currentTab={currentTab}
                        showClassificationSummary={currentTab === "NEW_ADDITIONS"}
                        selectedFundingPeriod={selectedFundingPeriod}
                        onClassificationSelect={handleClassificationSummarySelect}
                        onFilterSelect={handleVisualisationFilterSelect}
                      />
                      {crunchbaseVisualisationFilterSnapshot}
                    </>
                  )
                ) : rightPanelView === "network" ? (
                  <NetworkGraph
                    apiEnabled={DATA_SOURCE === "api"}
                    companies={companiesWithOverrides}
                    filters={networkFilters}
                    getApiJson={getApiJson}
                    setSidebarCompanyNames={applyGraphCompanyFilter}
                  />
                ) : (
                  <>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                      {companyListSummaryLabel}: {sorted.length}
                    </Typography>
                    {sorted.length === 0 ? (
                      <Typography sx={{ mt: 3, fontWeight: 700, color: "#1f2937" }}>
                        {noCrunchbaseCompaniesMessage}
                      </Typography>
                    ) : (
                      sorted.map((c) => (
                      <CompanyCard
                        key={c.id}
                        company={c}
                        feedbackData={feedbackData}
                        setFeedbackData={setFeedbackData}
                        getApiJson={getApiJson}
                        onClassificationChange={handleClassificationChange}
                        postJson={postJson}
                        deleteJson={deleteJson}
                        onManualOverrideSaved={loadCompanies}
                        assignment={getAssignmentForCompanyName(c["Organization Name"])}
                        canManageAssignments={canManageAssignments}
                        assignableUsers={assignableUsers}
                        assignableUsersLoading={assignableUsersLoading}
                        onAssignmentsChanged={loadAssignments}
                        onContactedChanged={handleCrunchbaseContactedChange}
                          updatedWindowFrom={updatedWindowFrom}
                          updatedWindowTo={updatedWindowTo}
                          onOpenGlobaldataMatch={openGlobaldataCompanyMatch}
                          onOpenCompanyHouseMatch={openCompanyHouseCompanyMatch}
                        />
                      ))
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>
        </>
        )}
      </Container>

      <Snackbar
        open={Boolean(overrideNotice)}
        autoHideDuration={3000}
        onClose={() => setOverrideNotice("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOverrideNotice("")}
          severity="info"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {overrideNotice}
        </Alert>
      </Snackbar>
    </>
  );
}

export default App;
